import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight20Regular, Dismiss20Regular, Wand20Regular } from '@fluentui/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import axiosInstance from '../../apiServices/axios';
import {
    AI_WARDEN_DOCS_LIGHTWEIGHT,
    AI_WARDEN_FILTERS,
    aiWardenWorkstreamQuery,
} from '../../apiServices/api_routes';
import { AiWardenScopeFilters } from '../AiWarden';
import {
    buildWorkstreamIdsQuery,
    collectDescendantFolderIdsFromFlat,
    deriveSourceFlags,
    selectedFolderIds,
    selectedWorkstreamIds,
    SCOPE_SENTINEL,
    workstreamOptionsFromList,
} from '../../utils/aiWardenScope';
import { PostRagChat } from '../../redux/dataroomSlice';
import { isIntelligenceAskEnabled } from '../../redux/intelligenceSlice';
import { dispatchScopedRagAsk } from '../../utils/ragDispatch';
import { GetIRLDocData } from '../../redux/documentsSlice';
import AiWardenPdfPanel from './AiWardenPdfPanel';
import IrlCitationSidebar from './IrlCitationSidebar';
import ObservationCitationSidebar from './ObservationCitationSidebar';
import AiWardenCitationListItem from './AiWardenCitationListItem';
import AiWardenMarkdownReport from './AiWardenMarkdownReport';
import AiWardenAskPanel from './AiWardenAskPanel';
import AiWardenSummarizePanel from './AiWardenSummarizePanel';
import AiWardenKeypointsPanel from './AiWardenKeypointsPanel';
import AiWardenAssessPanel from './AiWardenAssessPanel';
import AiWardenRisksPanel from './AiWardenRisksPanel';
import AiWardenThemesPanel from './AiWardenThemesPanel';
import AiWardenGapsPanel from './AiWardenGapsPanel';
import AiWardenEvidenceCoveragePanel from './AiWardenEvidenceCoveragePanel';
import GroundingBadge from './intelligence/GroundingBadge';
import UnsupportedClaimsBanner from './intelligence/UnsupportedClaimsBanner';
import FilenameResolutionBanner from './intelligence/FilenameResolutionBanner';
import AccuracyTrustStrip from './intelligence/AccuracyTrustStrip';
import InsufficientEvidenceBanner from './intelligence/InsufficientEvidenceBanner';
import AccuracyDebugPanel from './intelligence/AccuracyDebugPanel';
import AccuracyEvidencePanel from './intelligence/AccuracyEvidencePanel';
import ObservationCardList from './intelligence/ObservationCardList';
import AccuracyAskLoader from './intelligence/AccuracyAskLoader';
import {
    buildIrlCitationDetail,
    buildObservationCitationDetail,
    getAiWardenCitationTargetId,
    getCitationDisplayTitle,
    hasOpenableIrlCitation,
    hasOpenableObservationCitation,
    isIrlCitation,
    isObservationCitation,
    normalizeCitationRefValue,
} from '../../utils/citationDisplay';
import { resolveAskIntelligenceContext } from '../../utils/intelligenceMeta';
import styles from './css/index.module.css';
import { withUkEnglish } from '../../constants/llmInstructions';

const ALL = '__ALL__';
const MODE_ASK = 'ask_ai';
const MODE_SUMMARY = 'summarize';
const MODE_KEYPOINTS = 'keypoints';
const MODE_ASSESS = 'assess_area';
const MODE_RISKS = 'risks_signals';
const MODE_THEMES = 'explore_themes';
const MODE_GAPS = 'find_gaps';
const MODE_EVIDENCE_COVERAGE = 'evidence_coverage';

/** RAG-backed summary for the file-preview panel (server retrieves chunks for file_ids scope). */
const FILE_INSIGHT_SUMMARY_QUESTION = withUkEnglish(
    'Using only the retrieved context from this file, return markdown with these sections: "## Executive summary" (3-5 short paragraphs), "## Key points" (4-8 concise bullet points), and "## Important sections" (4-8 bullet points naming important clauses/sections/areas with a short why-it-matters note). If context is fragmented, state that briefly.',
);
const SCOPE_SUMMARY_QUESTION = withUkEnglish(`Provide a detailed consultant-grade summary of the selected scope.

Your summary should:
- explain the purpose and nature of the content
- identify major themes and subject areas
- summarize key operational, commercial, technical, financial, legal, or governance information
- highlight notable metrics, obligations, commitments, risks, or dependencies
- identify areas that appear particularly important or material
- surface any obvious gaps or limitations in the available information

The summary should help a consultant understand the content without reading every document manually.

Include:
1. Executive Summary
2. Key Areas Covered
3. Important Observations
4. Material Risks or Concerns
5. Notable Missing Information
6. Limitations

Do not speculate beyond the retrieved evidence.`);
const SCOPE_KEYPOINTS_QUESTION = withUkEnglish(`Extract the most important and material points from the selected scope.
Focus on:
- critical business information
- operational details
- technical architecture or dependencies
- commercial obligations
- governance or compliance matters
- financial indicators
- customer, vendor, or contractual dependencies
- strategic direction
- commitments, assumptions, or constraints
Prioritize:
- high-signal information
- information likely to matter during diligence
- information with potential operational, financial, legal, or strategic impact
Avoid generic summaries.
Structure output as:
1. Key Points
2. Why Each Point Matters
3. Citations
4. Evidence Limitations`);
const SCOPE_ASSESS_QUESTION = withUkEnglish(`Assess the selected scope from a diligence perspective using ONLY the retrieved evidence.
Provide an evidence-backed assessment of:
- operational maturity
- technical maturity (where applicable)
- governance and process quality
- documentation quality
- evidence completeness
- organizational clarity
- commercial robustness
- dependency or concentration risks
- scalability and resilience indicators
- compliance and control indicators
Your assessment should:
- identify strengths
- identify weaknesses
- identify risks and concerns
- explain what appears well-supported versus poorly supported
- evaluate the confidence level of the assessment based on available evidence
Do not assume that missing evidence means something does not exist.
Structure output as:
1. Overall Assessment
2. Strengths
3. Weaknesses
4. Risks & Concerns
5. Evidence Quality Assessment
6. Confidence Level
7. Limitations / Gaps`);
const SCOPE_RISKS_QUESTION = withUkEnglish(`Identify key risks, red flags, weak signals, inconsistencies, and notable concerns within the selected scope.

Focus on:
- operational risks
- technical risks
- security or compliance concerns
- customer or vendor concentration
- governance weaknesses
- scalability limitations
- undocumented dependencies
- unsupported claims
- vague or incomplete evidence
- outdated information
- process immaturity
- contractual or commercial exposure
- organizational ambiguity

Where possible:
- explain why the issue matters
- explain the potential impact
- identify whether evidence is strong, weak, or incomplete

Do not overstate conclusions.

Structure output as:
1. Key Risks & Signals
2. Potential Impact
3. Supporting Evidence
4. Evidence Gaps / Uncertainty`);
const SCOPE_THEMES_QUESTION = withUkEnglish(`Identify and explain the major themes, topics, entities, patterns, and areas of focus within the selected scope.

Focus on:
- recurring concepts
- operational themes
- technical themes
- commercial themes
- strategic priorities
- repeated dependencies
- recurring risks
- organizational focus areas
- customer/vendor/platform references
- business capabilities and processes

Where applicable:
- group related themes together
- explain relationships between themes
- identify dominant versus secondary themes

Structure output as:
1. Major Themes
2. Key Entities / Technologies / Platforms
3. Recurring Patterns
4. Emerging Concerns or Dependencies
5. Limitations`);
const SCOPE_GAPS_QUESTION = withUkEnglish(`Identify potential gaps, weaknesses, missing evidence, and areas requiring further validation within the selected scope.
Focus on:
- missing documents
- unsupported claims
- lack of implementation evidence
- incomplete operational detail
- missing metrics or reporting
- weak governance evidence
- missing ownership or accountability
- missing technical detail
- outdated documentation
- insufficient process evidence
- unclear controls or oversight
- lack of supporting data
- incomplete customer/vendor information
- gaps that may limit diligence confidence
Important:
Do NOT conclude that the company lacks something simply because evidence is missing.
Instead state:
- “No supporting evidence was found”
- “Evidence appears limited”
- “This area may require further validation”
Structure output as:
1. Identified Gaps
2. Why Each Gap Matters
3. Potential Follow-Up Areas
4. Suggested Information Requests
5. Confidence / Evidence Limitations`);
const SCOPE_EVIDENCE_COVERAGE_QUESTION = withUkEnglish(`Evaluate the quality, depth, completeness, and reliability of the available evidence within the selected scope.

Assess:
- documentation completeness
- evidence depth
- specificity versus high-level statements
- operational substantiation
- governance substantiation
- technical substantiation
- recency of information
- consistency of evidence
- breadth of coverage across relevant areas
- whether evidence appears sufficient for diligence purposes

Classify overall evidence coverage as:
- Low
- Medium
- High

Explain:
- why the coverage received that classification
- which areas are well-supported
- which areas appear weak or incomplete
- how confident a consultant should be in assessing this area using current documentation

Structure output as:
1. Coverage Rating
2. Coverage Assessment
3. Strongly Supported Areas
4. Weak or Incomplete Areas
5. Confidence Indicator
6. Limitations`);

function sanitizeInsightMarkdown(value) {
    let s = String(value || '').trim();
    if (!s) return s;

    // Remove standalone triple-backtick fence lines anywhere in the block.
    s = s.replace(/^\s*```[a-zA-Z0-9_-]*\s*$/gm, '').trim();

    // Handle wrapped single-block forms that may survive newline variations.
    const wrapped = s.match(/^```[a-zA-Z0-9_-]*\s*([\s\S]*?)\s*```$/);
    if (wrapped) s = String(wrapped[1] || '').trim();

    // If most lines are uniformly indented, de-indent to avoid markdown code-block rendering.
    const lines = s.split('\n');
    const nonEmpty = lines.filter(l => l.trim().length > 0);
    const indentedCount = nonEmpty.filter(l => /^\s{4,}\S/.test(l)).length;
    if (nonEmpty.length > 0 && indentedCount / nonEmpty.length >= 0.7) {
        s = lines
            .map(l => l.replace(/^\s{0,4}/, ''))
            .join('\n')
            .trim();
    }

    return s;
}

function splitInsightSections(markdown, citations = [], chunks = []) {
    const text = String(markdown || '').trim();
    const out = {
        summaryMd: '',
        keyPoints: [],
        importantSections: [],
    };
    if (!text) return out;

    const lines = text.split('\n');
    let bucket = 'summary';
    const pushLine = line => {
        if (bucket === 'summary') out.summaryMd += `${line}\n`;
        else if (bucket === 'keyPoints') out.keyPoints.push(line);
        else if (bucket === 'importantSections') out.importantSections.push(line);
    };

    for (const line of lines) {
        const h = line.trim();
        if (/^#{1,6}\s*(executive\s+summary|summary)\s*$/i.test(h)) {
            bucket = 'summary';
            continue;
        }
        if (/^#{1,6}\s*key\s+points\s*$/i.test(h)) {
            bucket = 'keyPoints';
            continue;
        }
        if (/^#{1,6}\s*important\s+sections?\s*$/i.test(h)) {
            bucket = 'importantSections';
            continue;
        }
        pushLine(line);
    }

    const normalizeBullets = arr =>
        arr
            .map(x => String(x || '').trim())
            .filter(Boolean)
            .filter(Boolean);

    out.summaryMd = sanitizeInsightMarkdown(out.summaryMd.trim() || text);
    out.keyPoints = normalizeBullets(out.keyPoints);
    out.importantSections = normalizeBullets(out.importantSections);

    if (out.keyPoints.length === 0) {
        const fromSummary = out.summaryMd
            .split(/(?<=[.!?])\s+/)
            .map(x => x.trim())
            .filter(Boolean)
            .slice(0, 6);
        out.keyPoints = fromSummary;
    }

    if (out.importantSections.length === 0) {
        const refs = Array.isArray(citations) ? citations : [];
        out.importantSections = refs.slice(0, 8).map(c => {
            const linked = getCitationChunk(c, Array.isArray(chunks) ? chunks : []);
            const topic = (normalizeChunkTopics(linked?.chunk_topics || []).slice(0, 2) || []).join(', ');
            const base = `${c?.ref || 'Ref'} - ${getChunkFileName(c)}`;
            return topic ? `${base} (${topic})` : base;
        });
    }

    return out;
}

function flattenFolderRows(nodes, depth = 0, out = []) {
    if (!Array.isArray(nodes)) return out;
    for (const n of nodes) {
        if (!n?.id) continue;
        out.push({ id: n.id, name: n.name || `Folder ${n.id}`, depth });
        if (Array.isArray(n.children) && n.children.length) {
            flattenFolderRows(n.children, depth + 1, out);
        }
    }
    return out;
}

function collectDescendantFolderIds(nodes, targetId) {
    const ids = new Set();
    const walk = list => {
        if (!Array.isArray(list)) return false;
        for (const node of list) {
            if (node.id === targetId) {
                const addTree = x => {
                    ids.add(x.id);
                    (x.children || []).forEach(addTree);
                };
                addTree(node);
                return true;
            }
            if (walk(node.children || [])) return true;
        }
        return false;
    };
    walk(nodes);
    return ids;
}

function folderNameById(folders, fid) {
    if (fid == null) return null;
    const row = (folders || []).find(r => String(r.id) === String(fid));
    return row?.depth_path || row?.name || row?.label || null;
}

function getChunkFileName(item, linkedChunk, ctx) {
    if (ctx) return getCitationDisplayTitle(item, linkedChunk, ctx);
    return item?.file_name || item?.metadata_storage_name || item?.title || (item?.file_id != null ? `File ${item.file_id}` : 'Document');
}

function normalizeKeywordText(value) {
    const txt = String(value || '').trim();
    if (!txt) return '';
    if (txt.startsWith('{') && txt.endsWith('}')) {
        try {
            const parsed = JSON.parse(txt);
            if (parsed && typeof parsed === 'object') {
                return String(parsed.name || parsed.keyword || txt).trim();
            }
        } catch {
            return txt;
        }
    }
    return txt;
}

function takeTopFileKeywords(raw, limit = 6) {
    if (!Array.isArray(raw)) return [];
    const out = [];
    const seen = new Set();
    const normalizeLabel = value => normalizeKeywordText(value);
    for (const item of raw) {
        const label = typeof item === 'string' ? normalizeLabel(item) : item && typeof item === 'object' && typeof item.keyword === 'string' ? normalizeLabel(item.keyword) : '';
        if (!label) continue;
        const k = label.toLowerCase();
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(label);
        if (out.length >= limit) break;
    }
    return out;
}

function collectAllFileKeywords(raw) {
    if (!Array.isArray(raw)) return [];
    const out = [];
    for (const item of raw) {
        const label =
            typeof item === 'string'
                ? normalizeKeywordText(item)
                : item && typeof item === 'object' && typeof item.keyword === 'string'
                  ? normalizeKeywordText(item.keyword)
                  : item && typeof item === 'object'
                    ? normalizeKeywordText(item.name || item.keyword)
                    : '';
        if (label) out.push(label);
    }
    return out;
}

function chunkStatsForDoc(chunks, docId) {
    if (!Array.isArray(chunks) || docId == null) return { unique: 0, rows: 0 };
    const forFile = chunks.filter(c => String(c.file_id) === String(docId));
    const keys = new Set();
    for (const c of forFile) {
        const k = c.chunk_id != null ? String(c.chunk_id) : `${c.parent_id ?? ''}-${(c.chunk || '').slice(0, 80)}`;
        keys.add(k);
    }
    return { unique: keys.size, rows: forFile.length };
}

function extractSimilarityScore(item) {
    if (!item || typeof item !== 'object') return null;
    const raw = item.similarity_score ?? item.score ?? item.search_score ?? item['@search.rerankerScore'] ?? item['@search.score'];
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return n;
}

function formatSimilarityLabel(item) {
    const score = extractSimilarityScore(item);
    if (score == null) return null;
    if (score >= 0 && score <= 1) return `similarity ${(score * 100).toFixed(1)}%`;
    return `similarity ${score.toFixed(3)}`;
}

function getCitationSimilarityLabel(citation, chunks) {
    return formatSimilarityLabel(getCitationChunk(citation, chunks));
}

function getCitationChunk(citation, chunks) {
    if (!citation) return null;
    const list = Array.isArray(chunks) ? chunks : [];
    if (Number.isInteger(citation.merged_chunk_index) && list[citation.merged_chunk_index]) {
        return list[citation.merged_chunk_index];
    }
    if (citation.chunk_id) {
        const byChunkId = list.find(x => x.chunk_id === citation.chunk_id);
        if (byChunkId) return byChunkId;
    }
    const refNum = parseInt(String(citation.ref || '').replace(/[^\d]/g, ''), 10);
    if (!Number.isNaN(refNum) && list[refNum - 1]) {
        return list[refNum - 1];
    }
    const preview = (citation.text_preview || citation.chunk || '').trim();
    if (preview) {
        return {
            ...citation,
            chunk: preview,
            chunk_topics: citation.chunk_topics,
            chunk_intent: citation.chunk_intent,
            chunk_doc_type: citation.chunk_doc_type,
        };
    }
    return null;
}

/** Top chunk keywords for UI (prefers citation payload; falls back to linked Azure chunk). */
function takeTopChunkKeywords(citation, linkedChunk, limit = 5) {
    const preferCitation = Array.isArray(citation?.keywords) && citation.keywords.length > 0;
    const raw = preferCitation ? citation.keywords : Array.isArray(linkedChunk?.keywords) ? linkedChunk.keywords : [];
    const out = [];
    const seen = new Set();
    for (const k of raw) {
        const s = typeof k === 'string' ? k.trim() : k && typeof k === 'object' && typeof k.keyword === 'string' ? k.keyword.trim() : '';
        if (!s) continue;
        const lower = s.toLowerCase();
        if (seen.has(lower)) continue;
        seen.add(lower);
        out.push(s);
        if (out.length >= limit) break;
    }
    return out;
}

function normalizeChunkTopics(raw) {
    if (Array.isArray(raw)) {
        return raw
            .map(x => String(x).trim())
            .filter(Boolean)
            .map(x => JSON.parse(x)) // Parse the JSON strings into objects
            .map(x => x.name); // Extract the 'name' property
    }
    if (typeof raw === 'string' && raw.trim()) {
        return raw
            .split(',')
            .map(x => x.trim())
            .filter(Boolean)
            .map(x => JSON.parse(x)) // Parse the JSON strings into objects
            .map(x => x.name); // Extract the 'name' property
    }
    return [];
}

const AiWardenModal = ({ open, onOpenChange, projectId, workstreams = [] }) => {
    const dispatch = useDispatch();
    const pid = projectId || localStorage.getItem('project_id');

    const allWorkstreamIds = useMemo(
        () => (workstreams || []).map(w => String(w.id)).filter(Boolean),
        [workstreams],
    );

    const [selectedWorkstreams, setSelectedWorkstreams] = useState([]);
    const [selectedFolders, setSelectedFolders] = useState([]);
    const [selectedIrlTags, setSelectedIrlTags] = useState([]);
    const [selectedObsTags, setSelectedObsTags] = useState([]);
    const [scopeFlatFolders, setScopeFlatFolders] = useState([]);
    const [scopeFilterTags, setScopeFilterTags] = useState({ irl_tags: [], observation_tags: [] });
    const [selectedFileIds, setSelectedFileIds] = useState([]);
    const [modalFiles, setModalFiles] = useState([]);
    const [selectedKeywordFilters, setSelectedKeywordFilters] = useState([]);
    const [analysisMode, setAnalysisMode] = useState(MODE_ASK);
    const [scopeLoading, setScopeLoading] = useState(false);
    const [libraryFilter, setLibraryFilter] = useState('');
    const [question, setQuestion] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [askPipelineScope, setAskPipelineScope] = useState(null);
    const [askResults, setAskResults] = useState(null);
    const [accuracyMode, setAccuracyMode] = useState(false);
    const [summaryResults, setSummaryResults] = useState(null);
    const [keypointsResults, setKeypointsResults] = useState(null);
    const [assessResults, setAssessResults] = useState(null);
    const [risksResults, setRisksResults] = useState(null);
    const [themesResults, setThemesResults] = useState(null);
    const [gapsResults, setGapsResults] = useState(null);
    const [evidenceCoverageResults, setEvidenceCoverageResults] = useState(null);
    const [citationPanel, setCitationPanel] = useState(null);
    const [irlCitationPanel, setIrlCitationPanel] = useState(null);
    const [observationCitationPanel, setObservationCitationPanel] = useState(null);
    /** Right-rail file preview: PDF + RAG summary + chunk stats + keywords. */
    const [fileInsightPanel, setFileInsightPanel] = useState(null);
    const previewSplitOpen = Boolean(citationPanel || irlCitationPanel || observationCitationPanel || fileInsightPanel);

    const sourceFlags = useMemo(
        () => deriveSourceFlags(selectedIrlTags, selectedObsTags),
        [selectedIrlTags, selectedObsTags],
    );

    const effectiveWorkstreamIds = useMemo(
        () => selectedWorkstreamIds(selectedWorkstreams, allWorkstreamIds),
        [selectedWorkstreams, allWorkstreamIds],
    );

    const hasWorkstreamsSelected = effectiveWorkstreamIds.length > 0;

    const applyContextDefaults = useCallback(() => {
        setSelectedWorkstreams(workstreamOptionsFromList(workstreams));
        setSelectedFolders([]);
        setSelectedIrlTags([]);
        setSelectedObsTags([]);
        setSelectedFileIds([]);
        setQuestion('');
        setAnalysisMode(MODE_ASK);
        setLibraryFilter('');
        setAskResults(null);
        setSummaryResults(null);
        setKeypointsResults(null);
        setAssessResults(null);
        setRisksResults(null);
        setThemesResults(null);
        setGapsResults(null);
        setEvidenceCoverageResults(null);
        setAccuracyMode(false);
    }, [workstreams]);

    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (open && !wasOpenRef.current) {
            applyContextDefaults();
        }
        wasOpenRef.current = open;
    }, [open, applyContextDefaults]);

    useEffect(() => {
        if (!open || !workstreams?.length) return;
        setSelectedWorkstreams(prev => (prev.length ? prev : workstreamOptionsFromList(workstreams)));
    }, [open, workstreams]);

    useEffect(() => {
        if (!open) {
            setCitationPanel(null);
            setIrlCitationPanel(null);
            setObservationCitationPanel(null);
            setFileInsightPanel(null);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = e => {
            if (e.key !== 'Escape') return;
            if (citationPanel) {
                e.preventDefault();
                setCitationPanel(null);
            } else if (irlCitationPanel) {
                e.preventDefault();
                setIrlCitationPanel(null);
            } else if (observationCitationPanel) {
                e.preventDefault();
                setObservationCitationPanel(null);
            } else if (fileInsightPanel) {
                e.preventDefault();
                setFileInsightPanel(null);
            } else {
                onOpenChange?.(false);
            }
        };
        window.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onOpenChange, citationPanel, irlCitationPanel, observationCitationPanel, fileInsightPanel]);

    useEffect(() => {
        if (!open || !pid || !hasWorkstreamsSelected) {
            if (!hasWorkstreamsSelected) {
                setScopeFlatFolders([]);
                setModalFiles([]);
            }
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setScopeLoading(true);
            try {
                const wsQueryIds = buildWorkstreamIdsQuery(selectedWorkstreams, allWorkstreamIds);
                const q = aiWardenWorkstreamQuery(wsQueryIds);
                const [filtersRes, filesRes] = await Promise.all([
                    axiosInstance.get(`${AI_WARDEN_FILTERS(pid)}${q}`),
                    axiosInstance.get(`${AI_WARDEN_DOCS_LIGHTWEIGHT(pid)}${q}`),
                ]);
                if (cancelled) return;
                const filtersData = filtersRes.data || {};
                setScopeFlatFolders(filtersData.folders || []);
                setScopeFilterTags({
                    irl_tags: filtersData.irl_tags || [],
                    observation_tags: filtersData.observation_tags || [],
                });
                const fd = filesRes.data;
                const files = Array.isArray(fd?.documents) ? fd.documents : [];
                setModalFiles(files);
            } catch (e) {
                if (!cancelled) {
                    toast.error('Could not load scope filters or files.');
                    setScopeFlatFolders([]);
                    setModalFiles([]);
                    setScopeFilterTags({ irl_tags: [], observation_tags: [] });
                }
            } finally {
                if (!cancelled) setScopeLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, pid, selectedWorkstreams, allWorkstreamIds, hasWorkstreamsSelected]);

    const filteredFiles = useMemo(() => {
        if (!modalFiles?.length) return [];
        const folderNums = selectedFolderIds(selectedFolders);
        if (!folderNums?.length) return modalFiles;
        const allowed = collectDescendantFolderIdsFromFlat(scopeFlatFolders, folderNums);
        if (!allowed.size) return modalFiles;
        return modalFiles.filter(f => allowed.has(Number(f.ws_folder_id)));
    }, [modalFiles, selectedFolders, scopeFlatFolders]);

    const scopeKeywords = useMemo(() => {
        if (!hasWorkstreamsSelected) return [];
        const seen = new Set();
        const out = [];
        for (const doc of filteredFiles) {
            for (const kw of collectAllFileKeywords(doc?.keywords)) {
                const k = kw.toLowerCase();
                if (!k || seen.has(k)) continue;
                seen.add(k);
                out.push(kw);
            }
        }
        out.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        return out;
    }, [filteredFiles, hasWorkstreamsSelected]);

    useEffect(() => {
        if (!selectedKeywordFilters.length) return;
        const allowed = new Set(scopeKeywords.map(k => k.toLowerCase()));
        setSelectedKeywordFilters(prev => prev.filter(k => allowed.has(String(k).toLowerCase())));
    }, [scopeKeywords, selectedKeywordFilters.length]);

    const libraryDocsPreKeyword = useMemo(() => {
        const q = libraryFilter.trim().toLowerCase();
        if (!q) return filteredFiles;
        return filteredFiles.filter(f => {
            const name = (f.document_name || f.name || '').toLowerCase();
            const folder = String(f.ws_folder || '').toLowerCase();
            return name.includes(q) || folder.includes(q) || String(f.id).includes(q);
        });
    }, [filteredFiles, libraryFilter]);

    const libraryDocs = useMemo(() => {
        if (!selectedKeywordFilters.length) return libraryDocsPreKeyword;
        const wanted = new Set(selectedKeywordFilters.map(s => String(s).toLowerCase()));
        return libraryDocsPreKeyword.filter(doc => {
            const kws = collectAllFileKeywords(doc?.keywords).map(x => x.toLowerCase());
            if (!kws.length) return false;
            for (const kw of kws) {
                if (wanted.has(kw)) return true; // OR filter: any selected keyword matches
            }
            return false;
        });
    }, [libraryDocsPreKeyword, selectedKeywordFilters]);

    useEffect(() => {
        if (!selectedFileIds.length) return;
        const allowed = new Set(filteredFiles.map(f => String(f.id)));
        setSelectedFileIds(prev => prev.filter(id => allowed.has(String(id))));
    }, [filteredFiles, selectedFileIds.length]);

    const activeWsName = useMemo(() => {
        if (effectiveWorkstreamIds.length !== 1) return null;
        const ws = (workstreams || []).find(w => String(w.id) === String(effectiveWorkstreamIds[0]));
        return ws?.name || null;
    }, [workstreams, effectiveWorkstreamIds]);

    const toggleFileSelection = useCallback(fileId => {
        const sid = String(fileId);
        setSelectedFileIds(prev => (prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]));
    }, []);

    const buildFolderIdsPayload = useCallback(() => {
        const rootIds = selectedFolderIds(selectedFolders);
        if (!rootIds?.length) return null;
        const allowed = collectDescendantFolderIdsFromFlat(scopeFlatFolders, rootIds);
        if (!allowed.size) return null;
        return Array.from(allowed).map(id => Number(id));
    }, [selectedFolders, scopeFlatFolders]);

    const resolveActiveScope = useCallback(() => {
        const wsNums = effectiveWorkstreamIds.map(Number).filter(Number.isFinite);
        const folderNums = buildFolderIdsPayload();
        const selectedFileNums = selectedFileIds.map(id => Number(id)).filter(Number.isFinite);
        const inScopeFileNums = filteredFiles.map(f => Number(f?.id)).filter(Number.isFinite);

        const base = {
            workstreamIds: wsNums,
            ...sourceFlags,
        };

        if (selectedFileNums.length === 1) {
            return {
                ...base,
                type: 'file',
                fileIds: selectedFileNums,
                folderIds: folderNums,
                label: 'Selected file',
            };
        }
        if (selectedFileNums.length > 1) {
            return {
                ...base,
                type: 'files',
                fileIds: selectedFileNums,
                folderIds: folderNums,
                label: `${selectedFileNums.length} files`,
            };
        }
        if (folderNums?.length) {
            return {
                ...base,
                type: 'folder',
                fileIds: inScopeFileNums,
                folderIds: folderNums,
                label: 'Selected folders',
            };
        }
        if (wsNums.length === allWorkstreamIds.length) {
            return {
                ...base,
                type: 'all_workstreams',
                fileIds: null,
                folderIds: null,
                label: 'All workstreams',
            };
        }
        return {
            ...base,
            type: 'workstream',
            fileIds: null,
            folderIds: folderNums,
            label: wsNums.length === 1 ? 'Selected workstream' : `${wsNums.length} workstreams`,
        };
    }, [
        effectiveWorkstreamIds,
        buildFolderIdsPayload,
        selectedFileIds,
        filteredFiles,
        allWorkstreamIds.length,
        sourceFlags,
    ]);

    const buildRequestBodyForMode = useCallback(
        (mode, scope) => {
            if (mode === MODE_SUMMARY) {
                const body = {
                    question: SCOPE_SUMMARY_QUESTION,
                    top: 120,
                };
                if (scope.fileIds?.length) {
                    body.file_ids = scope.fileIds;
                    // Full-document paging should be limited to single-file scope.
                    if (scope.fileIds.length === 1) {
                        body.retrieve_all_chunks = true;
                    }
                }
                if (scope.folderIds?.length) body.folder_ids = scope.folderIds;
                if (scope.workstreamIds?.length) body.workstream_ids = scope.workstreamIds;
                return body;
            }
            if (mode === MODE_KEYPOINTS) {
                const body = {
                    question: SCOPE_KEYPOINTS_QUESTION,
                    top: 120,
                };
                if (scope.fileIds?.length) {
                    body.file_ids = scope.fileIds;
                    if (scope.fileIds.length === 1) {
                        body.retrieve_all_chunks = true;
                    }
                }
                if (scope.folderIds?.length) body.folder_ids = scope.folderIds;
                if (scope.workstreamIds?.length) body.workstream_ids = scope.workstreamIds;
                return body;
            }
            if (mode === MODE_ASSESS) {
                const body = {
                    question: SCOPE_ASSESS_QUESTION,
                    top: 120,
                };
                if (scope.fileIds?.length) {
                    body.file_ids = scope.fileIds;
                    if (scope.fileIds.length === 1) {
                        body.retrieve_all_chunks = true;
                    }
                }
                if (scope.folderIds?.length) body.folder_ids = scope.folderIds;
                if (scope.workstreamIds?.length) body.workstream_ids = scope.workstreamIds;
                return body;
            }
            if (mode === MODE_RISKS) {
                const body = {
                    question: SCOPE_RISKS_QUESTION,
                    top: 120,
                };
                if (scope.fileIds?.length) {
                    body.file_ids = scope.fileIds;
                    if (scope.fileIds.length === 1) {
                        body.retrieve_all_chunks = true;
                    }
                }
                if (scope.folderIds?.length) body.folder_ids = scope.folderIds;
                if (scope.workstreamIds?.length) body.workstream_ids = scope.workstreamIds;
                return body;
            }
            if (mode === MODE_THEMES) {
                const body = {
                    question: SCOPE_THEMES_QUESTION,
                    top: 120,
                };
                if (scope.fileIds?.length) {
                    body.file_ids = scope.fileIds;
                    if (scope.fileIds.length === 1) {
                        body.retrieve_all_chunks = true;
                    }
                }
                if (scope.folderIds?.length) body.folder_ids = scope.folderIds;
                if (scope.workstreamIds?.length) body.workstream_ids = scope.workstreamIds;
                return body;
            }
            if (mode === MODE_GAPS) {
                const body = {
                    question: SCOPE_GAPS_QUESTION,
                    top: 120,
                };
                if (scope.fileIds?.length) {
                    body.file_ids = scope.fileIds;
                    if (scope.fileIds.length === 1) {
                        body.retrieve_all_chunks = true;
                    }
                }
                if (scope.folderIds?.length) body.folder_ids = scope.folderIds;
                if (scope.workstreamIds?.length) body.workstream_ids = scope.workstreamIds;
                return body;
            }
            if (mode === MODE_EVIDENCE_COVERAGE) {
                const body = {
                    question: SCOPE_EVIDENCE_COVERAGE_QUESTION,
                    top: 120,
                };
                if (scope.fileIds?.length) {
                    body.file_ids = scope.fileIds;
                    if (scope.fileIds.length === 1) {
                        body.retrieve_all_chunks = true;
                    }
                }
                if (scope.folderIds?.length) body.folder_ids = scope.folderIds;
                if (scope.workstreamIds?.length) body.workstream_ids = scope.workstreamIds;
                return body;
            }

            const q = question.trim();
            if (!q) return null;
            const body = { question: q, top: 8 };
            if (scope.fileIds?.length) body.file_ids = scope.fileIds;
            if (scope.folderIds?.length) body.folder_ids = scope.folderIds;
            if (scope.workstreamIds?.length) body.workstream_ids = scope.workstreamIds;
            if (body.file_ids?.length === 1 && /\b(summary|summarize|summarise|overview|digest)\b/i.test(q)) {
                body.retrieve_all_chunks = true;
            }
            return body;
        },
        [question],
    );

    const submitByMode = async mode => {
        if (!pid) return toast.error('No project selected.');
        if (mode === MODE_ASK && !question.trim()) return toast.error('Enter a question.');

        setSubmitting(true);
        if (mode === MODE_ASK) {
            setAskResults(null);
            const scopeForLoader = resolveActiveScope();
            setAskPipelineScope({
                type: scopeForLoader.type,
                workstreamIds: scopeForLoader.workstreamIds,
                fileIds: scopeForLoader.fileIds,
                folderIds: scopeForLoader.folderIds,
                label: scopeForLoader.label,
            });
        }
        else if (mode === MODE_SUMMARY) setSummaryResults(null);
        else if (mode === MODE_KEYPOINTS) setKeypointsResults(null);
        else if (mode === MODE_ASSESS) setAssessResults(null);
        else if (mode === MODE_RISKS) setRisksResults(null);
        else if (mode === MODE_THEMES) setThemesResults(null);
        else if (mode === MODE_GAPS) setGapsResults(null);
        else setEvidenceCoverageResults(null);
        try {
            const scope = resolveActiveScope();
            if (!scope.workstreamIds?.length) {
                toast.error('No workstreams available.');
                setSubmitting(false);
                return;
            }
            const body = buildRequestBodyForMode(mode, scope);
            if (!body) {
                toast.error('Please complete the request.');
                setSubmitting(false);
                return;
            }

            const ragResult = await dispatchScopedRagAsk({
                dispatch,
                projectId: pid,
                wsId: scope.workstreamIds?.length === 1 ? scope.workstreamIds[0] : undefined,
                scope,
                body,
                preferIntelligence: true,
                accuracyMode: mode === MODE_ASK ? accuracyMode : false,
            });

            const next = {
                mode: 'single',
                data: ragResult.data,
                requestMode: mode,
                scopeType: scope.type,
                scopeLabel: scope.label,
                usedIntelligenceAsk: ragResult.usedIntelligenceAsk,
                usedRagFallback: ragResult.usedRagFallback,
                intelligenceMeta: ragResult.intelligenceMeta,
                accuracyMode: mode === MODE_ASK ? accuracyMode : false,
            };
            if (mode === MODE_ASK) setAskResults(next);
            else if (mode === MODE_SUMMARY) setSummaryResults(next);
            else if (mode === MODE_KEYPOINTS) setKeypointsResults(next);
            else if (mode === MODE_ASSESS) setAssessResults(next);
            else if (mode === MODE_RISKS) setRisksResults(next);
            else if (mode === MODE_THEMES) setThemesResults(next);
            else if (mode === MODE_GAPS) setGapsResults(next);
            else setEvidenceCoverageResults(next);
        } catch (e) {
            const msg = e?.detail || e?.error || (typeof e === 'string' ? e : 'Request failed');
            toast.error(typeof msg === 'string' ? msg : 'RAG request failed');
            if (mode === MODE_ASK) setAskResults({ mode: 'error', message: String(msg) });
            else if (mode === MODE_SUMMARY) setSummaryResults({ mode: 'error', message: String(msg) });
            else if (mode === MODE_KEYPOINTS) setKeypointsResults({ mode: 'error', message: String(msg) });
            else if (mode === MODE_ASSESS) setAssessResults({ mode: 'error', message: String(msg) });
            else if (mode === MODE_RISKS) setRisksResults({ mode: 'error', message: String(msg) });
            else if (mode === MODE_THEMES) setThemesResults({ mode: 'error', message: String(msg) });
            else if (mode === MODE_GAPS) setGapsResults({ mode: 'error', message: String(msg) });
            else setEvidenceCoverageResults({ mode: 'error', message: String(msg) });
        } finally {
            setSubmitting(false);
        }
    };

    const handleAskSubmit = () => {
        void submitByMode(MODE_ASK);
    };

    const handleSummarizeSubmit = () => {
        void submitByMode(MODE_SUMMARY);
    };
    const handleKeypointsSubmit = () => {
        void submitByMode(MODE_KEYPOINTS);
    };
    const handleAssessSubmit = () => {
        void submitByMode(MODE_ASSESS);
    };
    const handleRisksSubmit = () => {
        void submitByMode(MODE_RISKS);
    };
    const handleThemesSubmit = () => {
        void submitByMode(MODE_THEMES);
    };
    const handleGapsSubmit = () => {
        void submitByMode(MODE_GAPS);
    };
    const handleEvidenceCoverageSubmit = () => {
        void submitByMode(MODE_EVIDENCE_COVERAGE);
    };

    const lastIntelligenceAsk = useSelector(state => state.intelligence?.lastAsk);
    const askChunks = useMemo(() => (askResults?.mode === 'single' && askResults.data?.chunks ? askResults.data.chunks : []), [askResults]);
    const askCitations = useMemo(() => (askResults?.mode === 'single' && askResults.data?.citations ? askResults.data.citations : []), [askResults]);
    const askIntelligenceContext = useMemo(
        () => resolveAskIntelligenceContext(askResults, lastIntelligenceAsk),
        [askResults, lastIntelligenceAsk],
    );

    const citationDisplayCtx = useMemo(
        () => ({
            workstreams,
            folderRows: scopeFlatFolders,
            modalFiles,
            activeWsName,
        }),
        [workstreams, scopeFlatFolders, modalFiles, activeWsName],
    );
    const summaryChunks = useMemo(() => (summaryResults?.mode === 'single' && summaryResults.data?.chunks ? summaryResults.data.chunks : []), [summaryResults]);
    const summaryCitations = useMemo(() => (summaryResults?.mode === 'single' && summaryResults.data?.citations ? summaryResults.data.citations : []), [summaryResults]);
    const keypointsChunks = useMemo(() => (keypointsResults?.mode === 'single' && keypointsResults.data?.chunks ? keypointsResults.data.chunks : []), [keypointsResults]);
    const keypointsCitations = useMemo(() => (keypointsResults?.mode === 'single' && keypointsResults.data?.citations ? keypointsResults.data.citations : []), [keypointsResults]);
    const assessChunks = useMemo(() => (assessResults?.mode === 'single' && assessResults.data?.chunks ? assessResults.data.chunks : []), [assessResults]);
    const assessCitations = useMemo(() => (assessResults?.mode === 'single' && assessResults.data?.citations ? assessResults.data.citations : []), [assessResults]);
    const risksChunks = useMemo(() => (risksResults?.mode === 'single' && risksResults.data?.chunks ? risksResults.data.chunks : []), [risksResults]);
    const risksCitations = useMemo(() => (risksResults?.mode === 'single' && risksResults.data?.citations ? risksResults.data.citations : []), [risksResults]);
    const themesChunks = useMemo(() => (themesResults?.mode === 'single' && themesResults.data?.chunks ? themesResults.data.chunks : []), [themesResults]);
    const themesCitations = useMemo(() => (themesResults?.mode === 'single' && themesResults.data?.citations ? themesResults.data.citations : []), [themesResults]);
    const gapsChunks = useMemo(() => (gapsResults?.mode === 'single' && gapsResults.data?.chunks ? gapsResults.data.chunks : []), [gapsResults]);
    const gapsCitations = useMemo(() => (gapsResults?.mode === 'single' && gapsResults.data?.citations ? gapsResults.data.citations : []), [gapsResults]);
    const evidenceCoverageChunks = useMemo(
        () => (evidenceCoverageResults?.mode === 'single' && evidenceCoverageResults.data?.chunks ? evidenceCoverageResults.data.chunks : []),
        [evidenceCoverageResults],
    );
    const evidenceCoverageCitations = useMemo(
        () =>
            evidenceCoverageResults?.mode === 'single' && evidenceCoverageResults.data?.citations
                ? evidenceCoverageResults.data.citations
                : [],
        [evidenceCoverageResults],
    );

    const canOpenCitationPdf = c => {
        if (!c?.file_id) return false;
        return c?.workstream_id != null || effectiveWorkstreamIds.length > 0;
    };

    const resolveCitationForScope = useCallback(
        (scope, ref, citation) => {
            if (citation) return citation;
            const normalized = normalizeCitationRefValue(ref);
            if (!normalized) return null;
            const lists = {
                ask: askCitations,
                summary: summaryCitations,
                keypoints: keypointsCitations,
                assess: assessCitations,
                risks: risksCitations,
                themes: themesCitations,
                gaps: gapsCitations,
                coverage: evidenceCoverageCitations,
                'file-insight': fileInsightPanel?.summaryCitations ?? [],
            };
            const list = lists[scope] || [];
            return list.find(item => normalizeCitationRefValue(item?.ref) === normalized) || null;
        },
        [
            askCitations,
            summaryCitations,
            keypointsCitations,
            assessCitations,
            risksCitations,
            themesCitations,
            gapsCitations,
            evidenceCoverageCitations,
            fileInsightPanel,
        ],
    );

    const openIrlCitationPanel = useCallback(
        (c, linkedChunk) => {
            setFileInsightPanel(null);
            setCitationPanel(null);
            setObservationCitationPanel(null);
            const detail = buildIrlCitationDetail(c, linkedChunk, citationDisplayCtx);
            setIrlCitationPanel({
                detail,
                token: `irl-${detail.irl_ref || detail.irl_id || c?.ref || 'cite'}-${Date.now()}`,
            });
        },
        [citationDisplayCtx],
    );

    const openObservationCitationPanel = useCallback((c, linkedChunk) => {
        setFileInsightPanel(null);
        setCitationPanel(null);
        setIrlCitationPanel(null);
        const detail = buildObservationCitationDetail(c, linkedChunk);
        setObservationCitationPanel({
            detail,
            token: `obs-${detail.observation_ref || detail.observation_id || c?.ref || 'cite'}-${Date.now()}`,
        });
    }, []);

    const handleIrlCitationClick = useCallback(
        (c, linkedChunk) => {
            if (!hasOpenableIrlCitation(c, linkedChunk)) {
                toast.error('This IRL citation has no detail metadata to show.');
                return;
            }
            openIrlCitationPanel(c, linkedChunk);
        },
        [openIrlCitationPanel],
    );

    const handleObservationCitationClick = useCallback(
        (c, linkedChunk) => {
            if (!hasOpenableObservationCitation(c, linkedChunk)) {
                toast.error('This observation citation has no detail metadata to show.');
                return;
            }
            openObservationCitationPanel(c, linkedChunk);
        },
        [openObservationCitationPanel],
    );

    const inlineCitationChunksByScope = useMemo(
        () => ({
            ask: askChunks,
            summary: summaryChunks,
            keypoints: keypointsChunks,
            assess: assessChunks,
            risks: risksChunks,
            themes: themesChunks,
            gaps: gapsChunks,
            coverage: evidenceCoverageChunks,
            'file-insight': fileInsightPanel?.summaryChunks ?? [],
        }),
        [
            askChunks,
            summaryChunks,
            keypointsChunks,
            assessChunks,
            risksChunks,
            themesChunks,
            gapsChunks,
            evidenceCoverageChunks,
            fileInsightPanel?.summaryChunks,
        ],
    );

    const handleInlineCitationClick = useCallback(
        (scope, ref, citation, linkedChunk, sourceChunks) => {
            const chunksForScope =
                Array.isArray(sourceChunks) && sourceChunks.length > 0
                    ? sourceChunks
                    : inlineCitationChunksByScope[scope] || [];
            const c = resolveCitationForScope(scope, ref, citation);
            const chunk = linkedChunk ?? getCitationChunk(c, chunksForScope);
            if (c && isObservationCitation(c, chunk) && hasOpenableObservationCitation(c, chunk)) {
                openObservationCitationPanel(c, chunk);
                return;
            }
            if (c && isIrlCitation(c, chunk) && hasOpenableIrlCitation(c, chunk)) {
                openIrlCitationPanel(c, chunk);
                return;
            }

            const targetId = getAiWardenCitationTargetId(scope, ref);
            const target = targetId ? document.getElementById(targetId) : null;
            if (!target) return;

            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            target.classList.remove(styles.aiWardenCitationCardPulse);
            void target.offsetWidth;
            target.classList.add(styles.aiWardenCitationCardPulse);
            window.setTimeout(() => target.classList.remove(styles.aiWardenCitationCardPulse), 1800);
        },
        [resolveCitationForScope, openIrlCitationPanel, openObservationCitationPanel, inlineCitationChunksByScope],
    );

    const handleCitationClick = useCallback(
        async (c, sourceChunks = []) => {
            const linkedChunk = getCitationChunk(c, sourceChunks);
            if (isObservationCitation(c, linkedChunk)) {
                handleObservationCitationClick(c, linkedChunk);
                return;
            }
            if (isIrlCitation(c, linkedChunk)) {
                handleIrlCitationClick(c, linkedChunk);
                return;
            }

            const fid = c?.file_id;
            if (fid == null) return toast.error('This citation has no file id.');
            const resolvedWsId = c?.workstream_id ?? effectiveWorkstreamIds[0];
            if (resolvedWsId == null) return toast.error('This citation has no workstream context.');

            let doc = modalFiles.find(f => String(f.id) === String(fid));
            if (!doc?.document) {
                try {
                    const res = await dispatch(GetIRLDocData({ id: fid, workstream: Number(resolvedWsId) })).unwrap();
                    doc = res?.data;
                } catch {
                    return toast.error('Could not load this file for viewing.');
                }
            }
            if (!doc?.document) return toast.error('This file has no PDF or blob to open in the viewer.');

            const chunkText = linkedChunk?.chunk || '';

            const pageRaw = c?.page_number ?? c?.page;
            let pageOneBased = null;
            if (pageRaw != null && `${pageRaw}` !== '') {
                const n = Number(pageRaw);
                if (!Number.isNaN(n)) pageOneBased = n + 1;
            }
            setFileInsightPanel(null);
            setIrlCitationPanel(null);
            setObservationCitationPanel(null);
            setCitationPanel({
                doc,
                highlightText: chunkText,
                pageOneBased,
                token: `${doc.id}-${c.chunk_id ?? c.ref ?? 'cite'}-${Date.now()}`,
            });
        },
        [modalFiles, dispatch, effectiveWorkstreamIds, handleIrlCitationClick, handleObservationCitationClick],
    );

    const renderCitationListItems = useCallback(
        (citations, chunks, scope) =>
            (citations || []).map(c => {
                const linkedChunk = getCitationChunk(c, chunks);
                return (
                    <AiWardenCitationListItem
                        key={`${scope}-${c.ref}-${c.chunk_id || linkedChunk?.chunk_id || 'na'}`}
                        citation={c}
                        linkedChunk={linkedChunk}
                        citationDisplayCtx={citationDisplayCtx}
                        scope={scope}
                        canOpenVdr={canOpenCitationPdf(c)}
                        canOpenIrl={hasOpenableIrlCitation(c, linkedChunk)}
                        canOpenObservation={hasOpenableObservationCitation(c, linkedChunk)}
                        onVdrOpen={cit => handleCitationClick(cit, chunks)}
                        onIrlOpen={handleIrlCitationClick}
                        onObservationOpen={handleObservationCitationClick}
                        getCitationSimilarityLabel={citation => getCitationSimilarityLabel(citation, chunks)}
                        normalizeChunkTopics={normalizeChunkTopics}
                        takeTopChunkKeywords={takeTopChunkKeywords}
                    />
                );
            }),
        [
            citationDisplayCtx,
            canOpenCitationPdf,
            handleCitationClick,
            handleIrlCitationClick,
            handleObservationCitationClick,
        ],
    );

    const openFileInsights = useCallback(
        async doc => {
            if (!doc?.id) return toast.error('Invalid file.');
            if (!pid) return toast.error('No project selected.');

            const wid = Number(doc.workstream_id ?? effectiveWorkstreamIds[0]);
            if (!Number.isFinite(wid)) return toast.error('This file has no workstream context for preview.');

            setCitationPanel(null);
            setIrlCitationPanel(null);
            setObservationCitationPanel(null);

            const fromList = modalFiles.find(f => String(f.id) === String(doc.id));
            let resolved = fromList ? { ...fromList } : { ...doc };
            if (!resolved?.document) {
                try {
                    const res = await dispatch(GetIRLDocData({ id: doc.id, workstream: wid })).unwrap();
                    if (res?.data) resolved = { ...resolved, ...res.data };
                } catch {
                    /* insights use RAG + stored keywords only; viewer opens from citations */
                }
            }

            const token = `file-insight-${resolved.id}-${Date.now()}`;
            setFileInsightPanel({
                doc: resolved,
                token,
                loading: true,
                summaryMd: '',
                keyPoints: [],
                importantSections: [],
                chunkUnique: null,
                summaryCitations: [],
                summaryChunks: [],
                summaryNotice: null,
                error: null,
            });

            try {
                const data = await dispatch(
                    PostRagChat({
                        projectId: pid,
                        wsId: wid,
                        body: {
                            question: FILE_INSIGHT_SUMMARY_QUESTION,
                            file_ids: [Number(resolved.id)],
                            workstream_ids: [wid],
                            retrieve_all_chunks: true,
                            top: 100,
                        },
                    }),
                ).unwrap();

                const chunks = Array.isArray(data?.chunks) ? data.chunks : [];
                const { unique } = chunkStatsForDoc(chunks, resolved.id);
                const summaryCitations = Array.isArray(data?.citations) ? data.citations : [];

                let summaryMd = '';
                if (typeof data?.final_answer === 'string' && data.final_answer.trim()) {
                    summaryMd = data.final_answer.trim();
                } else if (typeof data?.message === 'string' && data.message.trim()) {
                    summaryMd = data.message.trim();
                }
                const sectioned = splitInsightSections(summaryMd, summaryCitations, chunks);

                const synthErr = data?.synthesis_error ? String(data.synthesis_error) : '';
                const summaryNotice = synthErr || (!sectioned.summaryMd.trim() ? 'No summary was produced for this retrieval. Try asking a question in the chat area.' : null);

                setFileInsightPanel(prev =>
                    prev && prev.token === token
                        ? {
                              ...prev,
                              loading: false,
                              summaryMd: sectioned.summaryMd,
                              keyPoints: sectioned.keyPoints,
                              importantSections: sectioned.importantSections,
                              chunkUnique: unique,
                              summaryCitations,
                              summaryChunks: chunks,
                              summaryNotice: summaryNotice || null,
                              error: null,
                          }
                        : prev,
                );
            } catch (e) {
                const msg = e?.detail || e?.error || e?.message || 'Could not summarize this document.';
                setFileInsightPanel(prev =>
                    prev && prev.token === token
                        ? {
                              ...prev,
                              loading: false,
                              error: typeof msg === 'string' ? msg : 'Could not summarize this document.',
                              summaryMd: '',
                              keyPoints: [],
                              importantSections: [],
                              chunkUnique: null,
                              summaryCitations: [],
                              summaryChunks: [],
                              summaryNotice: null,
                          }
                        : prev,
                );
            }
        },
        [dispatch, modalFiles, pid, effectiveWorkstreamIds],
    );

    const askAnswerMarkdown = useMemo(() => {
        if (askResults?.mode !== 'single' || !askResults?.data) return '';
        const raw = String(askResults.data.final_answer || '').trim();
        if (!raw) return '';
        return sanitizeInsightMarkdown(raw);
    }, [askResults]);
    const summarizeAnswerMarkdown = useMemo(() => {
        if (summaryResults?.mode !== 'single' || !summaryResults?.data) return '';
        const raw = String(summaryResults.data.final_answer || '').trim();
        if (!raw) return '';
        return sanitizeInsightMarkdown(raw);
    }, [summaryResults]);
    const keypointsAnswerMarkdown = useMemo(() => {
        if (keypointsResults?.mode !== 'single' || !keypointsResults?.data) return '';
        const raw = String(keypointsResults.data.final_answer || '').trim();
        if (!raw) return '';
        return sanitizeInsightMarkdown(raw);
    }, [keypointsResults]);
    const assessAnswerMarkdown = useMemo(() => {
        if (assessResults?.mode !== 'single' || !assessResults?.data) return '';
        const raw = String(assessResults.data.final_answer || '').trim();
        if (!raw) return '';
        return sanitizeInsightMarkdown(raw);
    }, [assessResults]);
    const risksAnswerMarkdown = useMemo(() => {
        if (risksResults?.mode !== 'single' || !risksResults?.data) return '';
        const raw = String(risksResults.data.final_answer || '').trim();
        if (!raw) return '';
        return sanitizeInsightMarkdown(raw);
    }, [risksResults]);
    const themesAnswerMarkdown = useMemo(() => {
        if (themesResults?.mode !== 'single' || !themesResults?.data) return '';
        const raw = String(themesResults.data.final_answer || '').trim();
        if (!raw) return '';
        return sanitizeInsightMarkdown(raw);
    }, [themesResults]);
    const gapsAnswerMarkdown = useMemo(() => {
        if (gapsResults?.mode !== 'single' || !gapsResults?.data) return '';
        const raw = String(gapsResults.data.final_answer || '').trim();
        if (!raw) return '';
        return sanitizeInsightMarkdown(raw);
    }, [gapsResults]);
    const evidenceCoverageAnswerMarkdown = useMemo(() => {
        if (evidenceCoverageResults?.mode !== 'single' || !evidenceCoverageResults?.data) return '';
        const raw = String(evidenceCoverageResults.data.final_answer || '').trim();
        if (!raw) return '';
        return sanitizeInsightMarkdown(raw);
    }, [evidenceCoverageResults]);

    const handleWorkstreamsChange = useCallback(
        selected => {
            if ((selected || []).some(o => o?.value === SCOPE_SENTINEL.ALL)) {
                setSelectedWorkstreams(workstreamOptionsFromList(workstreams));
            } else {
                setSelectedWorkstreams(selected || []);
            }
            setSelectedFileIds([]);
        },
        [workstreams],
    );

    if (!open) return null;

    const toggleKeywordFilter = kw => {
        setSelectedKeywordFilters(prev => {
            const next = [...prev];
            const idx = next.findIndex(x => String(x).toLowerCase() === String(kw).toLowerCase());
            if (idx >= 0) next.splice(idx, 1);
            else next.push(kw);
            return next;
        });
    };

    const libraryScopeEl = (
        <AiWardenScopeFilters
            workstreams={workstreams}
            folders={scopeFlatFolders}
            irlTags={scopeFilterTags.irl_tags}
            observationTags={scopeFilterTags.observation_tags}
            selectedWorkstreams={selectedWorkstreams}
            selectedFolders={selectedFolders}
            selectedIrlTags={selectedIrlTags}
            selectedObsTags={selectedObsTags}
            onWorkstreamsChange={handleWorkstreamsChange}
            onFoldersChange={next => {
                setSelectedFolders(next || []);
                setSelectedFileIds([]);
            }}
            onIrlTagsChange={setSelectedIrlTags}
            onObsTagsChange={setSelectedObsTags}
            disabled={scopeLoading}
        />
    );

    const librarySearchEl = (
        <div className={styles.aiWardenSearchRow}>
            <input
                type="search"
                className={styles.aiWardenSearchInput}
                placeholder="Filter by title…"
                value={libraryFilter}
                onChange={e => setLibraryFilter(e.target.value)}
                disabled={!hasWorkstreamsSelected}
            />
            {libraryFilter ? (
                <button type="button" className={styles.aiWardenGhostBtn} onClick={() => setLibraryFilter('')}>
                    Clear
                </button>
            ) : null}
        </div>
    );

    const keywordFilterEl =
        hasWorkstreamsSelected && scopeKeywords.length > 0 ? (
            <div className={styles.aiWardenKeywordFilterWrap} aria-label="Keyword filters">
                <div className={styles.aiWardenKeywordFilterHeader}>
                    <span className={styles.aiWardenLibraryFilesKicker}>Filter by keywords</span>
                    {selectedKeywordFilters.length > 0 ? (
                        <button type="button" className={styles.aiWardenGhostBtn} onClick={() => setSelectedKeywordFilters([])}>
                            Clear ({selectedKeywordFilters.length})
                        </button>
                    ) : null}
                </div>
                <div className={styles.aiWardenKeywordFilterBar} role="list">
                    {scopeKeywords.map(kw => {
                        const selected = selectedKeywordFilters.some(x => String(x).toLowerCase() === String(kw).toLowerCase());
                        return (
                            <span
                                key={`kw-filter-${kw}`}
                                role="button"
                                tabIndex={0}
                                aria-pressed={selected}
                                className={`${styles.aiWardenKeywordChip} ${styles.aiWardenKeywordChipBtn} ${selected ? styles.aiWardenKeywordChipSelected : ''}`}
                                onClick={e => {
                                    e.preventDefault();
                                    toggleKeywordFilter(kw);
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggleKeywordFilter(kw);
                                    }
                                }}>
                                {kw}
                            </span>
                        );
                    })}
                </div>
            </div>
        ) : null;

    const libraryLoadingEl =
        scopeLoading && hasWorkstreamsSelected ? (
            <p className={styles.aiWardenLoadingLine}>
                <AiOutlineLoading3Quarters className="rotateLoader" /> Loading…
            </p>
        ) : null;

    const libraryDocListEl = (
        <div className={styles.aiWardenDocList}>
            {!hasWorkstreamsSelected && (
                <p className={styles.aiWardenEmpty}>Select at least one workstream to browse files.</p>
            )}
            {hasWorkstreamsSelected && !scopeLoading && libraryDocs.length === 0 && (
                <p className={styles.aiWardenEmpty}>No files match this scope.</p>
            )}
            {hasWorkstreamsSelected &&
                libraryDocs.map(doc => {
                    const selected = selectedFileIds.includes(String(doc.id));
                    const fname = folderNameById(scopeFlatFolders, doc.ws_folder_id);
                    const wsName =
                        (workstreams || []).find(w => String(w.id) === String(doc.workstream_id))?.name ||
                        (effectiveWorkstreamIds.length === 1 ? activeWsName : null);
                    const docKeywords = takeTopFileKeywords(doc.keywords, 6);
                    const title = doc.document_name || doc.name || doc.ws_folder || `File ${doc.id}`;
                    return (
                        <div key={doc.id} className={`${styles.aiWardenDocCardRow} ${selected ? styles.aiWardenDocCardRowActive : ''}`}>
                            <button type="button" className={styles.aiWardenDocCardMain} onClick={() => toggleFileSelection(doc.id)}>
                                <div className={styles.aiWardenDocTitle}>{title}</div>
                                <div className={styles.aiWardenDocMeta}>
                                    {wsName && <span className={styles.aiWardenChip}>{wsName}</span>}
                                    {fname && <span className={styles.aiWardenChip}>{fname}</span>}
                                </div>
                                {docKeywords.length > 0 && (
                                    <div className={styles.aiWardenDocKeywords} aria-label="File keywords">
                                        {docKeywords.map(kw => (
                                            <span key={`${doc.id}-${kw}`} className={styles.aiWardenKeywordChip}>
                                                {kw}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </button>
                            <button
                                type="button"
                                className={styles.aiWardenDocPeekBtn}
                                title="Open preview on the right"
                                aria-label={`Open ${title} on the right`}
                                onClick={e => {
                                    e.preventDefault();
                                    void openFileInsights(doc);
                                }}>
                                <ChevronRight20Regular />
                            </button>
                        </div>
                    );
                })}
        </div>
    );

    const libraryFootHintEl = (
        <p className={styles.aiWardenFootHint}>
            Tip: select files to narrow retrieval, or use folder and tag filters in the panel above.
        </p>
    );

    const fileInsightKeywordChips = fileInsightPanel ? takeTopFileKeywords(fileInsightPanel.doc?.keywords, 40) : [];
    const fileInsightSummaryCitations = fileInsightPanel?.summaryCitations ?? [];
    const fileInsightSummaryChunks = fileInsightPanel?.summaryChunks ?? [];
    const fileInsightKeyPoints = fileInsightPanel?.keyPoints ?? [];
    const fileInsightImportantSections = fileInsightPanel?.importantSections ?? [];
    const fileInsightKeyPointsMd = fileInsightKeyPoints
        .map(line => {
            const clean = String(line || '').trim();
            if (!clean) return '';
            return /^[-*•]\s+/.test(clean) ? clean.replace(/^•\s+/, '- ') : `- ${clean}`;
        })
        .filter(Boolean)
        .join('\n');
    const fileInsightImportantSectionsMd = fileInsightImportantSections
        .map(line => {
            const clean = String(line || '').trim();
            if (!clean) return '';
            return /^[-*•]\s+/.test(clean) ? clean.replace(/^•\s+/, '- ') : `- ${clean}`;
        })
        .filter(Boolean)
        .join('\n');
    return createPortal(
        <div className={styles.aiWardenShell} role="dialog" aria-modal="true" aria-labelledby="ai-warden-title">
            <div className={styles.aiWardenRoot}>
                <header className={styles.aiWardenHeader}>
                    <div className={styles.aiWardenHeaderBrand}>
                        <Wand20Regular />
                        <div>
                            <h2 id="ai-warden-title" className={styles.aiWardenTitle}>
                                AI Warden
                            </h2>
                        </div>
                    </div>
                    <span type="button" onClick={() => onOpenChange?.(false)} aria-label="Close">
                        <Dismiss20Regular />
                    </span>
                </header>

                <div className={`${styles.aiWardenMain} ${previewSplitOpen ? styles.aiWardenMainCitationSplit : ''}`}>
                    <div className={styles.aiWardenGridContents}>
                        <aside className={`${styles.aiWardenLibrary} ${previewSplitOpen ? styles.aiWardenLibraryPdfOpen : ''}`}>
                            <div className={styles.aiWardenPanelTop}>
                                <span className={styles.aiWardenKicker}>Library</span>
                                <span className={styles.aiWardenCountBadge}>
                                    {hasWorkstreamsSelected ? `${libraryDocs.length} docs` : 'No workstreams'}
                                </span>
                            </div>

                            <>
                                {libraryScopeEl}
                                {librarySearchEl}
                                {keywordFilterEl}
                                {libraryLoadingEl}
                                {libraryDocListEl}
                                {libraryFootHintEl}
                            </>
                        </aside>

                        <main className={styles.aiWardenAskColumn}>
                            {/* <div className={styles.aiWardenAskTop}>
                                <span className={styles.aiWardenKicker}>Ask</span>
                                <button type="button" className={styles.aiWardenAskFab} onClick={handleSubmit} disabled={submitting}>
                                    {submitting ? <AiOutlineLoading3Quarters className="rotateLoader" /> : 'Ask'}
                                </button>
                            </div> */}

                            <section className={styles.aiWardenAskSection}>
                                <label className={styles.aiWardenSectionLabel}>Mode</label>
                                <div className={styles.aiWardenModeTabs}>
                                    <button
                                        type="button"
                                        className={`${styles.aiWardenModeTab} ${analysisMode === MODE_ASK ? styles.aiWardenModeTabActive : ''}`}
                                        onClick={() => setAnalysisMode(MODE_ASK)}>
                                        Ask AI
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.aiWardenModeTab} ${analysisMode === MODE_SUMMARY ? styles.aiWardenModeTabActive : ''}`}
                                        onClick={() => setAnalysisMode(MODE_SUMMARY)}>
                                        Summarize
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.aiWardenModeTab} ${analysisMode === MODE_KEYPOINTS ? styles.aiWardenModeTabActive : ''}`}
                                        onClick={() => setAnalysisMode(MODE_KEYPOINTS)}>
                                        Keypoints
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.aiWardenModeTab} ${analysisMode === MODE_ASSESS ? styles.aiWardenModeTabActive : ''}`}
                                        onClick={() => setAnalysisMode(MODE_ASSESS)}>
                                        Assess This Area
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.aiWardenModeTab} ${analysisMode === MODE_RISKS ? styles.aiWardenModeTabActive : ''}`}
                                        onClick={() => setAnalysisMode(MODE_RISKS)}>
                                        Key Risks & Signals
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.aiWardenModeTab} ${analysisMode === MODE_THEMES ? styles.aiWardenModeTabActive : ''}`}
                                        onClick={() => setAnalysisMode(MODE_THEMES)}>
                                        Explore Themes
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.aiWardenModeTab} ${analysisMode === MODE_GAPS ? styles.aiWardenModeTabActive : ''}`}
                                        onClick={() => setAnalysisMode(MODE_GAPS)}>
                                        Find Gaps
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.aiWardenModeTab} ${analysisMode === MODE_EVIDENCE_COVERAGE ? styles.aiWardenModeTabActive : ''}`}
                                        onClick={() => setAnalysisMode(MODE_EVIDENCE_COVERAGE)}>
                                        Evidence Coverage
                                    </button>
                                </div>
                                {analysisMode === MODE_ASK ? (
                                    <AiWardenAskPanel
                                        question={question}
                                        onQuestionChange={setQuestion}
                                        onSubmit={handleAskSubmit}
                                        submitting={submitting}
                                        intelligenceAskEnabled={isIntelligenceAskEnabled()}
                                        accuracyMode={accuracyMode}
                                        onAccuracyModeChange={setAccuracyMode}
                                    />
                                ) : analysisMode === MODE_SUMMARY ? (
                                    <AiWardenSummarizePanel onSubmit={handleSummarizeSubmit} submitting={submitting} />
                                ) : analysisMode === MODE_KEYPOINTS ? (
                                    <AiWardenKeypointsPanel onSubmit={handleKeypointsSubmit} submitting={submitting} />
                                ) : analysisMode === MODE_ASSESS ? (
                                    <AiWardenAssessPanel onSubmit={handleAssessSubmit} submitting={submitting} />
                                ) : analysisMode === MODE_RISKS ? (
                                    <AiWardenRisksPanel onSubmit={handleRisksSubmit} submitting={submitting} />
                                ) : analysisMode === MODE_THEMES ? (
                                    <AiWardenThemesPanel onSubmit={handleThemesSubmit} submitting={submitting} />
                                ) : analysisMode === MODE_GAPS ? (
                                    <AiWardenGapsPanel onSubmit={handleGapsSubmit} submitting={submitting} />
                                ) : (
                                    <AiWardenEvidenceCoveragePanel onSubmit={handleEvidenceCoverageSubmit} submitting={submitting} />
                                )}
                            </section>

                            {analysisMode === MODE_ASK && submitting && isIntelligenceAskEnabled() && (
                                <AccuracyAskLoader
                                    active={submitting}
                                    scope={{
                                        includeIrl: sourceFlags.includeIrl,
                                        includeObservations: sourceFlags.includeObservations,
                                    }}
                                    scopeLabel={askPipelineScope?.label}
                                />
                            )}

                            {analysisMode === MODE_ASK && askResults?.mode === 'single' && askResults.data && (
                                <>
                                    {(!!(askResults.data.final_answer && String(askResults.data.final_answer).trim()) ||
                                        (typeof askResults.data.message === 'string' && askResults.data.message.trim())) && (
                                        <section className={styles.aiWardenAnswerSection}>
                                            <span className={styles.aiWardenKicker}>Answer</span>
                                            {askIntelligenceContext?.abstention?.isAbstention && (
                                                <InsufficientEvidenceBanner
                                                    title={askIntelligenceContext.abstention.title}
                                                    body={askIntelligenceContext.abstention.body}
                                                />
                                            )}
                                            <div className={styles.aiWardenAnswerCard}>
                                                {askAnswerMarkdown && !askIntelligenceContext?.abstention?.isAbstention ? (
                                                    <AiWardenMarkdownReport
                                                        content={askAnswerMarkdown}
                                                        citations={askCitations}
                                                        chunks={askChunks}
                                                        citationScope="ask"
                                                        onCitationClick={(ref, cite, chunk) =>
                                                            handleInlineCitationClick('ask', ref, cite, chunk)
                                                        }
                                                    />
                                                ) : (
                                                    <p className={styles.aiWardenInfo}>{String(askResults.data.message || '').trim()}</p>
                                                )}
                                            </div>
                                            {askIntelligenceContext?.showIntelligenceUi && (
                                                <div className={styles.aiWardenTrustRow}>
                                                    {askIntelligenceContext.hasAccuracyTrust && (
                                                        <AccuracyTrustStrip
                                                            retrievalDebug={askIntelligenceContext.retrieval_debug}
                                                            topLevelConfidence={
                                                                askResults.intelligenceMeta?.confidence ||
                                                                askResults.data?.confidence
                                                            }
                                                        />
                                                    )}
                                                    {askIntelligenceContext.hasGrounding && (
                                                        <>
                                                            <GroundingBadge
                                                                evidenceGovernance={askIntelligenceContext.evidence_governance}
                                                                retrievalDebug={askIntelligenceContext.retrieval_debug}
                                                            />
                                                            <UnsupportedClaimsBanner
                                                                evidenceGovernance={askIntelligenceContext.evidence_governance}
                                                                retrievalDebug={askIntelligenceContext.retrieval_debug}
                                                            />
                                                        </>
                                                    )}
                                                    {askIntelligenceContext.hasFilename && (
                                                        <FilenameResolutionBanner intelligenceContext={askIntelligenceContext} />
                                                    )}
                                                </div>
                                            )}
                                            {accuracyMode && askIntelligenceContext?.showIntelligenceUi && (
                                                <AccuracyEvidencePanel
                                                    accuracyMode={accuracyMode}
                                                    citations={askIntelligenceContext.citations}
                                                    evidence={askIntelligenceContext.evidence}
                                                    retrievalDebug={askIntelligenceContext.retrieval_debug}
                                                    chunks={askChunks}
                                                />
                                            )}
                                            {accuracyMode && askIntelligenceContext?.showIntelligenceUi && (
                                                <AccuracyDebugPanel
                                                    retrievalDebug={askIntelligenceContext.retrieval_debug}
                                                />
                                            )}
                                            <div className={styles.aiWardenStatRow}>
                                                {askCitations.length > 0 && <span className={styles.aiWardenStatPill}>Citations: {askCitations.length}</span>}
                                                {askResults.usedIntelligenceAsk && askResults.intelligenceMeta?.confidence && (
                                                    <span className={styles.aiWardenStatPill}>Confidence: {askResults.intelligenceMeta.confidence}</span>
                                                )}
                                                {askResults.usedIntelligenceAsk && askResults.intelligenceMeta?.trace_id && (
                                                    <span className={styles.aiWardenStatPill} title="Support trace id">
                                                        Trace: {askResults.intelligenceMeta.trace_id}
                                                    </span>
                                                )}
                                            </div>
                                        </section>
                                    )}
                                    {askIntelligenceContext?.observation_candidates?.length > 0 && (
                                        <ObservationCardList
                                            observations={askIntelligenceContext.observation_candidates}
                                            onCitationClick={(ref, cite, chunk) =>
                                                handleInlineCitationClick('ask', ref, cite, chunk)
                                            }
                                        />
                                    )}
                                    {askResults.data.synthesis_error && <p className={styles.aiWardenWarn}>{askResults.data.synthesis_error}</p>}

                                    {askCitations.length > 0 && (
                                        <div className={`${styles.aiWardenSplit}${previewSplitOpen ? ` ${styles.aiWardenSplitStacked}` : ''}`}>
                                            <div className={styles.aiWardenSplitCol}>
                                                <span className={styles.aiWardenKicker}>Citations</span>
                                                <ul className={styles.aiWardenCitationList}>{renderCitationListItems(askCitations, askChunks, 'ask')}</ul>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                            {analysisMode === MODE_SUMMARY && summaryResults?.mode === 'single' && summaryResults.data && (
                                <>
                                    {(!!(summaryResults.data.final_answer && String(summaryResults.data.final_answer).trim()) ||
                                        (typeof summaryResults.data.message === 'string' && summaryResults.data.message.trim())) && (
                                        <section className={styles.aiWardenAnswerSection}>
                                            <span className={styles.aiWardenKicker}>{`Summary${summaryResults.scopeLabel ? ` - ${summaryResults.scopeLabel}` : ''}`}</span>
                                            <div className={styles.aiWardenAnswerCard}>
                                                {summarizeAnswerMarkdown ? (
                                                    <AiWardenMarkdownReport
                                                        content={summarizeAnswerMarkdown}
                                                        citations={summaryCitations}
                                                        chunks={summaryChunks}
                                                        citationScope="summary"
                                                        onCitationClick={(ref, cite, chunk) =>
                                                            handleInlineCitationClick('summary', ref, cite, chunk)
                                                        }
                                                    />
                                                ) : (
                                                    <p className={styles.aiWardenInfo}>{String(summaryResults.data.message || '').trim()}</p>
                                                )}
                                            </div>
                                            <div className={styles.aiWardenStatRow}>
                                                {summaryCitations.length > 0 && <span className={styles.aiWardenStatPill}>Citations: {summaryCitations.length}</span>}
                                            </div>
                                        </section>
                                    )}
                                    {summaryResults.data.synthesis_error && <p className={styles.aiWardenWarn}>{summaryResults.data.synthesis_error}</p>}
                                    {summaryCitations.length > 0 && (
                                        <div className={`${styles.aiWardenSplit}${previewSplitOpen ? ` ${styles.aiWardenSplitStacked}` : ''}`}>
                                            <div className={styles.aiWardenSplitCol}>
                                                <span className={styles.aiWardenKicker}>Citations</span>
                                                <ul className={styles.aiWardenCitationList}>
                                                    {summaryCitations.map(c => {
                                                        const linkedChunk = getCitationChunk(c, summaryChunks);
                                                        const tooltipText = (linkedChunk?.chunk || '').trim();
                                                        const topKw = takeTopChunkKeywords(c, linkedChunk, 5);
                                                        const chunkTopics = normalizeChunkTopics(linkedChunk?.chunk_topics);
                                                        const chunkDocType = (linkedChunk?.chunk_doc_type || '').trim();
                                                        const chunkIntent = (linkedChunk?.chunk_intent || '').trim();
                                                        return (
                                                            <li
                                                                key={`summary-${c.ref}`}
                                                                id={getAiWardenCitationTargetId('summary', c)}
                                                                className={`${styles.aiWardenCitationCard}${canOpenCitationPdf(c) ? ` ${styles.aiWardenCitationCardInteractive}` : ''}`}
                                                                role={canOpenCitationPdf(c) ? 'button' : undefined}
                                                                tabIndex={canOpenCitationPdf(c) ? 0 : undefined}
                                                                onClick={() => canOpenCitationPdf(c) && handleCitationClick(c, summaryChunks)}
                                                                onKeyDown={e => {
                                                                    if (!canOpenCitationPdf(c)) return;
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        handleCitationClick(c, summaryChunks);
                                                                    }
                                                                }}>
                                                                <span className={styles.aiWardenCitationRef}>{c.ref}</span>
                                                                <span className={styles.aiWardenCitationTitle}>{getCitationDisplayTitle(c, linkedChunk, citationDisplayCtx)}</span>
                                                                {getCitationSimilarityLabel(c, summaryChunks) && (
                                                                    <span className={styles.aiWardenCitationMeta}>{getCitationSimilarityLabel(c, summaryChunks)}</span>
                                                                )}
                                                                {(chunkDocType || chunkIntent) && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk metadata">
                                                                        {chunkDocType && <p>Type: {chunkDocType}</p>}
                                                                        {chunkIntent && <p>Intent: {chunkIntent}</p>}
                                                                    </div>
                                                                )}
                                                                {chunkTopics.length > 0 && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk topics">
                                                                        {chunkTopics.map(topic => (
                                                                            <span key={`${c.ref}-s-topic-${topic}`} className={styles.aiWardenKeywordChip}>
                                                                                {topic}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {topKw.length > 0 && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk keywords">
                                                                        {topKw.map(kw => (
                                                                            <span key={`${c.ref}-s-kw-${kw}`} className={styles.aiWardenKeywordChip}>
                                                                                {kw}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {tooltipText && (
                                                                    <span className={styles.aiWardenCitationTooltip} role="tooltip">
                                                                        {tooltipText.length > 700 ? `${tooltipText.slice(0, 700)}…` : tooltipText}
                                                                    </span>
                                                                )}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                            {analysisMode === MODE_KEYPOINTS && keypointsResults?.mode === 'single' && keypointsResults.data && (
                                <>
                                    {(!!(keypointsResults.data.final_answer && String(keypointsResults.data.final_answer).trim()) ||
                                        (typeof keypointsResults.data.message === 'string' && keypointsResults.data.message.trim())) && (
                                        <section className={styles.aiWardenAnswerSection}>
                                            <span className={styles.aiWardenKicker}>{`Keypoints${keypointsResults.scopeLabel ? ` - ${keypointsResults.scopeLabel}` : ''}`}</span>
                                            <div className={styles.aiWardenAnswerCard}>
                                                {keypointsAnswerMarkdown ? (
                                                    <AiWardenMarkdownReport
                                                        content={keypointsAnswerMarkdown}
                                                        citations={keypointsCitations}
                                                        chunks={keypointsChunks}
                                                        citationScope="keypoints"
                                                        onCitationClick={(ref, cite, chunk) =>
                                                            handleInlineCitationClick('keypoints', ref, cite, chunk)
                                                        }
                                                    />
                                                ) : (
                                                    <p className={styles.aiWardenInfo}>{String(keypointsResults.data.message || '').trim()}</p>
                                                )}
                                            </div>
                                            <div className={styles.aiWardenStatRow}>
                                                {keypointsCitations.length > 0 && <span className={styles.aiWardenStatPill}>Citations: {keypointsCitations.length}</span>}
                                            </div>
                                        </section>
                                    )}
                                    {keypointsResults.data.synthesis_error && <p className={styles.aiWardenWarn}>{keypointsResults.data.synthesis_error}</p>}
                                    {keypointsCitations.length > 0 && (
                                        <div className={`${styles.aiWardenSplit}${previewSplitOpen ? ` ${styles.aiWardenSplitStacked}` : ''}`}>
                                            <div className={styles.aiWardenSplitCol}>
                                                <span className={styles.aiWardenKicker}>Citations</span>
                                                <ul className={styles.aiWardenCitationList}>
                                                    {keypointsCitations.map(c => {
                                                        const linkedChunk = getCitationChunk(c, keypointsChunks);
                                                        const tooltipText = (linkedChunk?.chunk || '').trim();
                                                        const topKw = takeTopChunkKeywords(c, linkedChunk, 5);
                                                        const chunkTopics = normalizeChunkTopics(linkedChunk?.chunk_topics);
                                                        const chunkDocType = (linkedChunk?.chunk_doc_type || '').trim();
                                                        const chunkIntent = (linkedChunk?.chunk_intent || '').trim();
                                                        return (
                                                            <li
                                                                key={`keypoints-${c.ref}`}
                                                                id={getAiWardenCitationTargetId('keypoints', c)}
                                                                className={`${styles.aiWardenCitationCard}${canOpenCitationPdf(c) ? ` ${styles.aiWardenCitationCardInteractive}` : ''}`}
                                                                role={canOpenCitationPdf(c) ? 'button' : undefined}
                                                                tabIndex={canOpenCitationPdf(c) ? 0 : undefined}
                                                                onClick={() => canOpenCitationPdf(c) && handleCitationClick(c, keypointsChunks)}
                                                                onKeyDown={e => {
                                                                    if (!canOpenCitationPdf(c)) return;
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        handleCitationClick(c, keypointsChunks);
                                                                    }
                                                                }}>
                                                                <span className={styles.aiWardenCitationRef}>{c.ref}</span>
                                                                <span className={styles.aiWardenCitationTitle}>{getCitationDisplayTitle(c, linkedChunk, citationDisplayCtx)}</span>
                                                                {getCitationSimilarityLabel(c, keypointsChunks) && (
                                                                    <span className={styles.aiWardenCitationMeta}>{getCitationSimilarityLabel(c, keypointsChunks)}</span>
                                                                )}
                                                                {(chunkDocType || chunkIntent) && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk metadata">
                                                                        {chunkDocType && <p>Type: {chunkDocType}</p>}
                                                                        {chunkIntent && <p>Intent: {chunkIntent}</p>}
                                                                    </div>
                                                                )}
                                                                {chunkTopics.length > 0 && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk topics">
                                                                        {chunkTopics.map(topic => (
                                                                            <span key={`${c.ref}-k-topic-${topic}`} className={styles.aiWardenKeywordChip}>
                                                                                {topic}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {topKw.length > 0 && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk keywords">
                                                                        {topKw.map(kw => (
                                                                            <span key={`${c.ref}-k-kw-${kw}`} className={styles.aiWardenKeywordChip}>
                                                                                {kw}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {tooltipText && (
                                                                    <span className={styles.aiWardenCitationTooltip} role="tooltip">
                                                                        {tooltipText.length > 700 ? `${tooltipText.slice(0, 700)}…` : tooltipText}
                                                                    </span>
                                                                )}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                            {analysisMode === MODE_ASSESS && assessResults?.mode === 'single' && assessResults.data && (
                                <>
                                    {(!!(assessResults.data.final_answer && String(assessResults.data.final_answer).trim()) ||
                                        (typeof assessResults.data.message === 'string' && assessResults.data.message.trim())) && (
                                        <section className={styles.aiWardenAnswerSection}>
                                            <span className={styles.aiWardenKicker}>{`Assessment${assessResults.scopeLabel ? ` - ${assessResults.scopeLabel}` : ''}`}</span>
                                            <div className={styles.aiWardenAnswerCard}>
                                                {assessAnswerMarkdown ? (
                                                    <AiWardenMarkdownReport
                                                        content={assessAnswerMarkdown}
                                                        citations={assessCitations}
                                                        chunks={assessChunks}
                                                        citationScope="assess"
                                                        onCitationClick={(ref, cite, chunk) =>
                                                            handleInlineCitationClick('assess', ref, cite, chunk)
                                                        }
                                                    />
                                                ) : (
                                                    <p className={styles.aiWardenInfo}>{String(assessResults.data.message || '').trim()}</p>
                                                )}
                                            </div>
                                            <div className={styles.aiWardenStatRow}>
                                                {assessCitations.length > 0 && <span className={styles.aiWardenStatPill}>Citations: {assessCitations.length}</span>}
                                            </div>
                                        </section>
                                    )}
                                    {assessResults.data.synthesis_error && <p className={styles.aiWardenWarn}>{assessResults.data.synthesis_error}</p>}
                                    {assessCitations.length > 0 && (
                                        <div className={`${styles.aiWardenSplit}${previewSplitOpen ? ` ${styles.aiWardenSplitStacked}` : ''}`}>
                                            <div className={styles.aiWardenSplitCol}>
                                                <span className={styles.aiWardenKicker}>Citations</span>
                                                <ul className={styles.aiWardenCitationList}>
                                                    {assessCitations.map(c => {
                                                        const linkedChunk = getCitationChunk(c, assessChunks);
                                                        const tooltipText = (linkedChunk?.chunk || '').trim();
                                                        const topKw = takeTopChunkKeywords(c, linkedChunk, 5);
                                                        const chunkTopics = normalizeChunkTopics(linkedChunk?.chunk_topics);
                                                        const chunkDocType = (linkedChunk?.chunk_doc_type || '').trim();
                                                        const chunkIntent = (linkedChunk?.chunk_intent || '').trim();
                                                        return (
                                                            <li
                                                                key={`assess-${c.ref}`}
                                                                id={getAiWardenCitationTargetId('assess', c)}
                                                                className={`${styles.aiWardenCitationCard}${canOpenCitationPdf(c) ? ` ${styles.aiWardenCitationCardInteractive}` : ''}`}
                                                                role={canOpenCitationPdf(c) ? 'button' : undefined}
                                                                tabIndex={canOpenCitationPdf(c) ? 0 : undefined}
                                                                onClick={() => canOpenCitationPdf(c) && handleCitationClick(c, assessChunks)}
                                                                onKeyDown={e => {
                                                                    if (!canOpenCitationPdf(c)) return;
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        handleCitationClick(c, assessChunks);
                                                                    }
                                                                }}>
                                                                <span className={styles.aiWardenCitationRef}>{c.ref}</span>
                                                                <span className={styles.aiWardenCitationTitle}>{getCitationDisplayTitle(c, linkedChunk, citationDisplayCtx)}</span>
                                                                {getCitationSimilarityLabel(c, assessChunks) && (
                                                                    <span className={styles.aiWardenCitationMeta}>{getCitationSimilarityLabel(c, assessChunks)}</span>
                                                                )}
                                                                {(chunkDocType || chunkIntent) && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk metadata">
                                                                        {chunkDocType && <p>Type: {chunkDocType}</p>}
                                                                        {chunkIntent && <p>Intent: {chunkIntent}</p>}
                                                                    </div>
                                                                )}
                                                                {chunkTopics.length > 0 && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk topics">
                                                                        {chunkTopics.map(topic => (
                                                                            <span key={`${c.ref}-a-topic-${topic}`} className={styles.aiWardenKeywordChip}>
                                                                                {topic}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {topKw.length > 0 && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk keywords">
                                                                        {topKw.map(kw => (
                                                                            <span key={`${c.ref}-a-kw-${kw}`} className={styles.aiWardenKeywordChip}>
                                                                                {kw}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {tooltipText && (
                                                                    <span className={styles.aiWardenCitationTooltip} role="tooltip">
                                                                        {tooltipText.length > 700 ? `${tooltipText.slice(0, 700)}…` : tooltipText}
                                                                    </span>
                                                                )}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                            {analysisMode === MODE_RISKS && risksResults?.mode === 'single' && risksResults.data && (
                                <>
                                    {(!!(risksResults.data.final_answer && String(risksResults.data.final_answer).trim()) ||
                                        (typeof risksResults.data.message === 'string' && risksResults.data.message.trim())) && (
                                        <section className={styles.aiWardenAnswerSection}>
                                            <span className={styles.aiWardenKicker}>{`Key Risks & Signals${risksResults.scopeLabel ? ` - ${risksResults.scopeLabel}` : ''}`}</span>
                                            <div className={styles.aiWardenAnswerCard}>
                                                {risksAnswerMarkdown ? (
                                                    <AiWardenMarkdownReport
                                                        content={risksAnswerMarkdown}
                                                        citations={risksCitations}
                                                        chunks={risksChunks}
                                                        citationScope="risks"
                                                        onCitationClick={(ref, cite, chunk) =>
                                                            handleInlineCitationClick('risks', ref, cite, chunk)
                                                        }
                                                    />
                                                ) : (
                                                    <p className={styles.aiWardenInfo}>{String(risksResults.data.message || '').trim()}</p>
                                                )}
                                            </div>
                                            <div className={styles.aiWardenStatRow}>
                                                {risksCitations.length > 0 && <span className={styles.aiWardenStatPill}>Citations: {risksCitations.length}</span>}
                                            </div>
                                        </section>
                                    )}
                                    {risksResults.data.synthesis_error && <p className={styles.aiWardenWarn}>{risksResults.data.synthesis_error}</p>}
                                    {risksCitations.length > 0 && (
                                        <div className={`${styles.aiWardenSplit}${previewSplitOpen ? ` ${styles.aiWardenSplitStacked}` : ''}`}>
                                            <div className={styles.aiWardenSplitCol}>
                                                <span className={styles.aiWardenKicker}>Citations</span>
                                                <ul className={styles.aiWardenCitationList}>
                                                    {risksCitations.map(c => {
                                                        const linkedChunk = getCitationChunk(c, risksChunks);
                                                        const tooltipText = (linkedChunk?.chunk || '').trim();
                                                        const topKw = takeTopChunkKeywords(c, linkedChunk, 5);
                                                        const chunkTopics = normalizeChunkTopics(linkedChunk?.chunk_topics);
                                                        const chunkDocType = (linkedChunk?.chunk_doc_type || '').trim();
                                                        const chunkIntent = (linkedChunk?.chunk_intent || '').trim();
                                                        return (
                                                            <li
                                                                key={`risks-${c.ref}`}
                                                                id={getAiWardenCitationTargetId('risks', c)}
                                                                className={`${styles.aiWardenCitationCard}${canOpenCitationPdf(c) ? ` ${styles.aiWardenCitationCardInteractive}` : ''}`}
                                                                role={canOpenCitationPdf(c) ? 'button' : undefined}
                                                                tabIndex={canOpenCitationPdf(c) ? 0 : undefined}
                                                                onClick={() => canOpenCitationPdf(c) && handleCitationClick(c, risksChunks)}
                                                                onKeyDown={e => {
                                                                    if (!canOpenCitationPdf(c)) return;
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        handleCitationClick(c, risksChunks);
                                                                    }
                                                                }}>
                                                                <span className={styles.aiWardenCitationRef}>{c.ref}</span>
                                                                <span className={styles.aiWardenCitationTitle}>{getCitationDisplayTitle(c, linkedChunk, citationDisplayCtx)}</span>
                                                                {getCitationSimilarityLabel(c, risksChunks) && (
                                                                    <span className={styles.aiWardenCitationMeta}>{getCitationSimilarityLabel(c, risksChunks)}</span>
                                                                )}
                                                                {(chunkDocType || chunkIntent) && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk metadata">
                                                                        {chunkDocType && <p>Type: {chunkDocType}</p>}
                                                                        {chunkIntent && <p>Intent: {chunkIntent}</p>}
                                                                    </div>
                                                                )}
                                                                {chunkTopics.length > 0 && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk topics">
                                                                        {chunkTopics.map(topic => (
                                                                            <span key={`${c.ref}-r-topic-${topic}`} className={styles.aiWardenKeywordChip}>
                                                                                {topic}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {topKw.length > 0 && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk keywords">
                                                                        {topKw.map(kw => (
                                                                            <span key={`${c.ref}-r-kw-${kw}`} className={styles.aiWardenKeywordChip}>
                                                                                {kw}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {tooltipText && (
                                                                    <span className={styles.aiWardenCitationTooltip} role="tooltip">
                                                                        {tooltipText.length > 700 ? `${tooltipText.slice(0, 700)}…` : tooltipText}
                                                                    </span>
                                                                )}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                            {analysisMode === MODE_THEMES && themesResults?.mode === 'single' && themesResults.data && (
                                <>
                                    {(!!(themesResults.data.final_answer && String(themesResults.data.final_answer).trim()) ||
                                        (typeof themesResults.data.message === 'string' && themesResults.data.message.trim())) && (
                                        <section className={styles.aiWardenAnswerSection}>
                                            <span className={styles.aiWardenKicker}>{`Explore Themes${themesResults.scopeLabel ? ` - ${themesResults.scopeLabel}` : ''}`}</span>
                                            <div className={styles.aiWardenAnswerCard}>
                                                {themesAnswerMarkdown ? (
                                                    <AiWardenMarkdownReport
                                                        content={themesAnswerMarkdown}
                                                        citations={themesCitations}
                                                        chunks={themesChunks}
                                                        citationScope="themes"
                                                        onCitationClick={(ref, cite, chunk) =>
                                                            handleInlineCitationClick('themes', ref, cite, chunk)
                                                        }
                                                    />
                                                ) : (
                                                    <p className={styles.aiWardenInfo}>{String(themesResults.data.message || '').trim()}</p>
                                                )}
                                            </div>
                                            <div className={styles.aiWardenStatRow}>
                                                {themesCitations.length > 0 && <span className={styles.aiWardenStatPill}>Citations: {themesCitations.length}</span>}
                                            </div>
                                        </section>
                                    )}
                                    {themesResults.data.synthesis_error && <p className={styles.aiWardenWarn}>{themesResults.data.synthesis_error}</p>}
                                    {themesCitations.length > 0 && (
                                        <div className={`${styles.aiWardenSplit}${previewSplitOpen ? ` ${styles.aiWardenSplitStacked}` : ''}`}>
                                            <div className={styles.aiWardenSplitCol}>
                                                <span className={styles.aiWardenKicker}>Citations</span>
                                                <ul className={styles.aiWardenCitationList}>
                                                    {themesCitations.map(c => {
                                                        const linkedChunk = getCitationChunk(c, themesChunks);
                                                        const tooltipText = (linkedChunk?.chunk || '').trim();
                                                        const topKw = takeTopChunkKeywords(c, linkedChunk, 5);
                                                        const chunkTopics = normalizeChunkTopics(linkedChunk?.chunk_topics);
                                                        const chunkDocType = (linkedChunk?.chunk_doc_type || '').trim();
                                                        const chunkIntent = (linkedChunk?.chunk_intent || '').trim();
                                                        return (
                                                            <li
                                                                key={`themes-${c.ref}`}
                                                                id={getAiWardenCitationTargetId('themes', c)}
                                                                className={`${styles.aiWardenCitationCard}${canOpenCitationPdf(c) ? ` ${styles.aiWardenCitationCardInteractive}` : ''}`}
                                                                role={canOpenCitationPdf(c) ? 'button' : undefined}
                                                                tabIndex={canOpenCitationPdf(c) ? 0 : undefined}
                                                                onClick={() => canOpenCitationPdf(c) && handleCitationClick(c, themesChunks)}
                                                                onKeyDown={e => {
                                                                    if (!canOpenCitationPdf(c)) return;
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        handleCitationClick(c, themesChunks);
                                                                    }
                                                                }}>
                                                                <span className={styles.aiWardenCitationRef}>{c.ref}</span>
                                                                <span className={styles.aiWardenCitationTitle}>{getCitationDisplayTitle(c, linkedChunk, citationDisplayCtx)}</span>
                                                                {getCitationSimilarityLabel(c, themesChunks) && (
                                                                    <span className={styles.aiWardenCitationMeta}>{getCitationSimilarityLabel(c, themesChunks)}</span>
                                                                )}
                                                                {(chunkDocType || chunkIntent) && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk metadata">
                                                                        {chunkDocType && <p>Type: {chunkDocType}</p>}
                                                                        {chunkIntent && <p>Intent: {chunkIntent}</p>}
                                                                    </div>
                                                                )}
                                                                {chunkTopics.length > 0 && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk topics">
                                                                        {chunkTopics.map(topic => (
                                                                            <span key={`${c.ref}-t-topic-${topic}`} className={styles.aiWardenKeywordChip}>
                                                                                {topic}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {topKw.length > 0 && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk keywords">
                                                                        {topKw.map(kw => (
                                                                            <span key={`${c.ref}-t-kw-${kw}`} className={styles.aiWardenKeywordChip}>
                                                                                {kw}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {tooltipText && (
                                                                    <span className={styles.aiWardenCitationTooltip} role="tooltip">
                                                                        {tooltipText.length > 700 ? `${tooltipText.slice(0, 700)}…` : tooltipText}
                                                                    </span>
                                                                )}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                            {analysisMode === MODE_GAPS && gapsResults?.mode === 'single' && gapsResults.data && (
                                <>
                                    {(!!(gapsResults.data.final_answer && String(gapsResults.data.final_answer).trim()) ||
                                        (typeof gapsResults.data.message === 'string' && gapsResults.data.message.trim())) && (
                                        <section className={styles.aiWardenAnswerSection}>
                                            <span className={styles.aiWardenKicker}>{`Find Gaps${gapsResults.scopeLabel ? ` - ${gapsResults.scopeLabel}` : ''}`}</span>
                                            <div className={styles.aiWardenAnswerCard}>
                                                {gapsAnswerMarkdown ? (
                                                    <AiWardenMarkdownReport
                                                        content={gapsAnswerMarkdown}
                                                        citations={gapsCitations}
                                                        chunks={gapsChunks}
                                                        citationScope="gaps"
                                                        onCitationClick={(ref, cite, chunk) =>
                                                            handleInlineCitationClick('gaps', ref, cite, chunk)
                                                        }
                                                    />
                                                ) : (
                                                    <p className={styles.aiWardenInfo}>{String(gapsResults.data.message || '').trim()}</p>
                                                )}
                                            </div>
                                            <div className={styles.aiWardenStatRow}>
                                                {gapsCitations.length > 0 && <span className={styles.aiWardenStatPill}>Citations: {gapsCitations.length}</span>}
                                            </div>
                                        </section>
                                    )}
                                    {gapsResults.data.synthesis_error && <p className={styles.aiWardenWarn}>{gapsResults.data.synthesis_error}</p>}
                                    {gapsCitations.length > 0 && (
                                        <div className={`${styles.aiWardenSplit}${previewSplitOpen ? ` ${styles.aiWardenSplitStacked}` : ''}`}>
                                            <div className={styles.aiWardenSplitCol}>
                                                <span className={styles.aiWardenKicker}>Citations</span>
                                                <ul className={styles.aiWardenCitationList}>
                                                    {gapsCitations.map(c => {
                                                        const linkedChunk = getCitationChunk(c, gapsChunks);
                                                        const tooltipText = (linkedChunk?.chunk || '').trim();
                                                        const topKw = takeTopChunkKeywords(c, linkedChunk, 5);
                                                        const chunkTopics = normalizeChunkTopics(linkedChunk?.chunk_topics);
                                                        const chunkDocType = (linkedChunk?.chunk_doc_type || '').trim();
                                                        const chunkIntent = (linkedChunk?.chunk_intent || '').trim();
                                                        return (
                                                            <li
                                                                key={`gaps-${c.ref}`}
                                                                id={getAiWardenCitationTargetId('gaps', c)}
                                                                className={`${styles.aiWardenCitationCard}${canOpenCitationPdf(c) ? ` ${styles.aiWardenCitationCardInteractive}` : ''}`}
                                                                role={canOpenCitationPdf(c) ? 'button' : undefined}
                                                                tabIndex={canOpenCitationPdf(c) ? 0 : undefined}
                                                                onClick={() => canOpenCitationPdf(c) && handleCitationClick(c, gapsChunks)}
                                                                onKeyDown={e => {
                                                                    if (!canOpenCitationPdf(c)) return;
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        handleCitationClick(c, gapsChunks);
                                                                    }
                                                                }}>
                                                                <span className={styles.aiWardenCitationRef}>{c.ref}</span>
                                                                <span className={styles.aiWardenCitationTitle}>{getCitationDisplayTitle(c, linkedChunk, citationDisplayCtx)}</span>
                                                                {getCitationSimilarityLabel(c, gapsChunks) && (
                                                                    <span className={styles.aiWardenCitationMeta}>{getCitationSimilarityLabel(c, gapsChunks)}</span>
                                                                )}
                                                                {(chunkDocType || chunkIntent) && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk metadata">
                                                                        {chunkDocType && <p>Type: {chunkDocType}</p>}
                                                                        {chunkIntent && <p>Intent: {chunkIntent}</p>}
                                                                    </div>
                                                                )}
                                                                {chunkTopics.length > 0 && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk topics">
                                                                        {chunkTopics.map(topic => (
                                                                            <span key={`${c.ref}-g-topic-${topic}`} className={styles.aiWardenKeywordChip}>
                                                                                {topic}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {topKw.length > 0 && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk keywords">
                                                                        {topKw.map(kw => (
                                                                            <span key={`${c.ref}-g-kw-${kw}`} className={styles.aiWardenKeywordChip}>
                                                                                {kw}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {tooltipText && (
                                                                    <span className={styles.aiWardenCitationTooltip} role="tooltip">
                                                                        {tooltipText.length > 700 ? `${tooltipText.slice(0, 700)}…` : tooltipText}
                                                                    </span>
                                                                )}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                            {analysisMode === MODE_EVIDENCE_COVERAGE && evidenceCoverageResults?.mode === 'single' && evidenceCoverageResults.data && (
                                <>
                                    {(!!(evidenceCoverageResults.data.final_answer && String(evidenceCoverageResults.data.final_answer).trim()) ||
                                        (typeof evidenceCoverageResults.data.message === 'string' && evidenceCoverageResults.data.message.trim())) && (
                                        <section className={styles.aiWardenAnswerSection}>
                                            <span className={styles.aiWardenKicker}>
                                                {`Evidence Coverage${evidenceCoverageResults.scopeLabel ? ` - ${evidenceCoverageResults.scopeLabel}` : ''}`}
                                            </span>
                                            <div className={styles.aiWardenAnswerCard}>
                                                {evidenceCoverageAnswerMarkdown ? (
                                                    <AiWardenMarkdownReport
                                                        content={evidenceCoverageAnswerMarkdown}
                                                        citations={evidenceCoverageCitations}
                                                        chunks={evidenceCoverageChunks}
                                                        citationScope="coverage"
                                                        onCitationClick={(ref, cite, chunk) =>
                                                            handleInlineCitationClick('coverage', ref, cite, chunk)
                                                        }
                                                    />
                                                ) : (
                                                    <p className={styles.aiWardenInfo}>{String(evidenceCoverageResults.data.message || '').trim()}</p>
                                                )}
                                            </div>
                                            <div className={styles.aiWardenStatRow}>
                                                {evidenceCoverageCitations.length > 0 && (
                                                    <span className={styles.aiWardenStatPill}>Citations: {evidenceCoverageCitations.length}</span>
                                                )}
                                            </div>
                                        </section>
                                    )}
                                    {evidenceCoverageResults.data.synthesis_error && <p className={styles.aiWardenWarn}>{evidenceCoverageResults.data.synthesis_error}</p>}
                                    {evidenceCoverageCitations.length > 0 && (
                                        <div className={`${styles.aiWardenSplit}${previewSplitOpen ? ` ${styles.aiWardenSplitStacked}` : ''}`}>
                                            <div className={styles.aiWardenSplitCol}>
                                                <span className={styles.aiWardenKicker}>Citations</span>
                                                <ul className={styles.aiWardenCitationList}>
                                                    {evidenceCoverageCitations.map(c => {
                                                        const linkedChunk = getCitationChunk(c, evidenceCoverageChunks);
                                                        const tooltipText = (linkedChunk?.chunk || '').trim();
                                                        const topKw = takeTopChunkKeywords(c, linkedChunk, 5);
                                                        const chunkTopics = normalizeChunkTopics(linkedChunk?.chunk_topics);
                                                        const chunkDocType = (linkedChunk?.chunk_doc_type || '').trim();
                                                        const chunkIntent = (linkedChunk?.chunk_intent || '').trim();
                                                        return (
                                                            <li
                                                                key={`coverage-${c.ref}`}
                                                                id={getAiWardenCitationTargetId('coverage', c)}
                                                                className={`${styles.aiWardenCitationCard}${canOpenCitationPdf(c) ? ` ${styles.aiWardenCitationCardInteractive}` : ''}`}
                                                                role={canOpenCitationPdf(c) ? 'button' : undefined}
                                                                tabIndex={canOpenCitationPdf(c) ? 0 : undefined}
                                                                onClick={() => canOpenCitationPdf(c) && handleCitationClick(c, evidenceCoverageChunks)}
                                                                onKeyDown={e => {
                                                                    if (!canOpenCitationPdf(c)) return;
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        handleCitationClick(c, evidenceCoverageChunks);
                                                                    }
                                                                }}>
                                                                <span className={styles.aiWardenCitationRef}>{c.ref}</span>
                                                                <span className={styles.aiWardenCitationTitle}>{getCitationDisplayTitle(c, linkedChunk, citationDisplayCtx)}</span>
                                                                {getCitationSimilarityLabel(c, evidenceCoverageChunks) && (
                                                                    <span className={styles.aiWardenCitationMeta}>{getCitationSimilarityLabel(c, evidenceCoverageChunks)}</span>
                                                                )}
                                                                {(chunkDocType || chunkIntent) && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk metadata">
                                                                        {chunkDocType && <p>Type: {chunkDocType}</p>}
                                                                        {chunkIntent && <p>Intent: {chunkIntent}</p>}
                                                                    </div>
                                                                )}
                                                                {chunkTopics.length > 0 && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk topics">
                                                                        {chunkTopics.map(topic => (
                                                                            <span key={`${c.ref}-c-topic-${topic}`} className={styles.aiWardenKeywordChip}>
                                                                                {topic}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {topKw.length > 0 && (
                                                                    <div className={styles.aiWardenCitationKeywords} aria-label="Chunk keywords">
                                                                        {topKw.map(kw => (
                                                                            <span key={`${c.ref}-c-kw-${kw}`} className={styles.aiWardenKeywordChip}>
                                                                                {kw}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                {tooltipText && (
                                                                    <span className={styles.aiWardenCitationTooltip} role="tooltip">
                                                                        {tooltipText.length > 700 ? `${tooltipText.slice(0, 700)}…` : tooltipText}
                                                                    </span>
                                                                )}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {analysisMode === MODE_ASK && askResults?.mode === 'error' && <p className={styles.aiWardenErr}>{askResults.message}</p>}
                            {analysisMode === MODE_SUMMARY && summaryResults?.mode === 'error' && <p className={styles.aiWardenErr}>{summaryResults.message}</p>}
                            {analysisMode === MODE_KEYPOINTS && keypointsResults?.mode === 'error' && <p className={styles.aiWardenErr}>{keypointsResults.message}</p>}
                            {analysisMode === MODE_ASSESS && assessResults?.mode === 'error' && <p className={styles.aiWardenErr}>{assessResults.message}</p>}
                            {analysisMode === MODE_RISKS && risksResults?.mode === 'error' && <p className={styles.aiWardenErr}>{risksResults.message}</p>}
                            {analysisMode === MODE_THEMES && themesResults?.mode === 'error' && <p className={styles.aiWardenErr}>{themesResults.message}</p>}
                            {analysisMode === MODE_GAPS && gapsResults?.mode === 'error' && <p className={styles.aiWardenErr}>{gapsResults.message}</p>}
                            {analysisMode === MODE_EVIDENCE_COVERAGE && evidenceCoverageResults?.mode === 'error' && (
                                <p className={styles.aiWardenErr}>{evidenceCoverageResults.message}</p>
                            )}
                        </main>
                    </div>

                    {fileInsightPanel ? (
                        <div key={fileInsightPanel.token} className={styles.aiWardenPdfColumn}>
                            <div className={styles.aiWardenPdfHeader}>
                                <div className={styles.aiWardenPdfTitleWrap}>
                                    <span className={styles.aiWardenKicker}>Document insights</span>
                                    <span className={styles.aiWardenPdfTitle} title={fileInsightPanel.doc?.document_name || fileInsightPanel.doc?.name}>
                                        {fileInsightPanel.doc?.document_name || fileInsightPanel.doc?.name || `File ${fileInsightPanel.doc?.id}`}
                                    </span>
                                </div>
                                <button type="button" className={styles.aiWardenPdfClose} onClick={() => setFileInsightPanel(null)} aria-label="Close document insights">
                                    <Dismiss20Regular />
                                </button>
                            </div>
                            <div className={styles.aiWardenInsightsOnlyBody}>
                                <div className={styles.aiWardenInsightsInner}>
                                    {fileInsightPanel.error ? (
                                        <p className={styles.aiWardenErr}>{fileInsightPanel.error}</p>
                                    ) : (
                                        <>
                                            <section className={styles.aiWardenInsightsSection} aria-label="Chunk count">
                                                {fileInsightPanel.loading ? (
                                                    <div className={styles.aiWardenInsightsChunkStat}>
                                                        <AiOutlineLoading3Quarters className="rotateLoader" /> <span className={styles.aiWardenInsightsMuted}>Fetching…</span>
                                                    </div>
                                                ) : (
                                                    <p className={styles.aiWardenInsightsChunkStat}>
                                                        {fileInsightPanel.chunkUnique != null ? (
                                                            <>
                                                                <span className={styles.aiWardenKeywordChip}>
                                                                    {fileInsightPanel.chunkUnique} chunk{fileInsightPanel.chunkUnique === 1 ? '' : 's'}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <span className={styles.aiWardenInsightsMuted}>No chunk stats</span>
                                                        )}
                                                    </p>
                                                )}
                                            </section>

                                            <section className={styles.aiWardenInsightsSection}>
                                                <h3 className={styles.aiWardenInsightsHeadline}>Executive summary</h3>
                                                <p className={styles.aiWardenInsightsSubhead}>AI-generated from retrieved document passages.</p>
                                                {fileInsightPanel.loading ? (
                                                    <p className={styles.aiWardenInsightsMuted}>Generating summary…</p>
                                                ) : fileInsightPanel.summaryMd ? (
                                                    <div className={styles.aiWardenInsightsSummary}>
                                                        <AiWardenMarkdownReport
                                                            content={fileInsightPanel.summaryMd}
                                                            citations={fileInsightSummaryCitations}
                                                            chunks={fileInsightSummaryChunks}
                                                            citationScope="file-insight"
                                                            onCitationClick={(ref, cite, chunk) =>
                                                                handleInlineCitationClick('file-insight', ref, cite, chunk)
                                                            }
                                                        />
                                                    </div>
                                                ) : (
                                                    !fileInsightPanel.summaryNotice && <p className={styles.aiWardenInsightsMuted}>No summary returned.</p>
                                                )}
                                                {fileInsightPanel.summaryNotice ? (
                                                    <p className={fileInsightPanel.summaryMd?.trim() ? styles.aiWardenInsightsMuted : styles.aiWardenWarn}>
                                                        {fileInsightPanel.summaryNotice}
                                                    </p>
                                                ) : null}
                                            </section>

                                            <section className={styles.aiWardenInsightsSection}>
                                                <h3 className={styles.aiWardenInsightsHeadline}>Key points</h3>
                                                {fileInsightPanel.loading ? (
                                                    <p className={styles.aiWardenInsightsMuted}>Extracting key points…</p>
                                                ) : fileInsightKeyPoints.length ? (
                                                    <div className={styles.aiWardenInsightsSummary}>
                                                        <AiWardenMarkdownReport
                                                            content={fileInsightKeyPointsMd}
                                                            citations={fileInsightSummaryCitations}
                                                            chunks={fileInsightSummaryChunks}
                                                            citationScope="file-insight"
                                                            onCitationClick={(ref, cite, chunk) =>
                                                                handleInlineCitationClick('file-insight', ref, cite, chunk)
                                                            }
                                                        />
                                                    </div>
                                                ) : (
                                                    <p className={styles.aiWardenInsightsMuted}>No key points found.</p>
                                                )}
                                            </section>

                                            <section className={styles.aiWardenInsightsSection}>
                                                <h3 className={styles.aiWardenInsightsHeadline}>Important sections</h3>
                                                {fileInsightPanel.loading ? (
                                                    <p className={styles.aiWardenInsightsMuted}>Identifying important sections…</p>
                                                ) : fileInsightImportantSections.length ? (
                                                    <div className={styles.aiWardenInsightsSummary}>
                                                        <AiWardenMarkdownReport
                                                            content={fileInsightImportantSectionsMd}
                                                            citations={fileInsightSummaryCitations}
                                                            chunks={fileInsightSummaryChunks}
                                                            citationScope="file-insight"
                                                            onCitationClick={(ref, cite, chunk) =>
                                                                handleInlineCitationClick('file-insight', ref, cite, chunk)
                                                            }
                                                        />
                                                    </div>
                                                ) : (
                                                    <p className={styles.aiWardenInsightsMuted}>No important sections identified.</p>
                                                )}
                                            </section>

                                            <section className={styles.aiWardenInsightsSection}>
                                                <h3 className={styles.aiWardenInsightsHeadline}>Keywords</h3>
                                                <p className={styles.aiWardenInsightsSubhead}>File-level rollup keywords.</p>
                                                {fileInsightKeywordChips.length === 0 ? (
                                                    <p className={styles.aiWardenInsightsMuted}>None stored yet for this file.</p>
                                                ) : (
                                                    <div className={styles.aiWardenInsightsChipRow} aria-label="Keywords">
                                                        {fileInsightKeywordChips.map((kw, i) => (
                                                            <span key={`${kw}-${i}`} className={styles.aiWardenKeywordChip}>
                                                                {kw}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </section>

                                            <section className={styles.aiWardenInsightsSection}>
                                                <h3 className={styles.aiWardenInsightsHeadline}>Citations</h3>
                                                {!fileInsightPanel.loading && fileInsightSummaryCitations.length > 0 ? (
                                                    <ul className={styles.aiWardenCitationList} aria-label="Summary citations">
                                                        {fileInsightSummaryCitations.map(c => {
                                                            const linkedChunk = getCitationChunk(c, fileInsightSummaryChunks);
                                                            const tooltipText = (linkedChunk?.chunk || '').trim();
                                                            const topKw = takeTopChunkKeywords(c, linkedChunk, 5);
                                                            const chunkTopics = normalizeChunkTopics(linkedChunk?.chunk_topics);
                                                            const chunkDocType = (linkedChunk?.chunk_doc_type || '').trim();
                                                            const chunkIntent = (linkedChunk?.chunk_intent || '').trim();
                                                            return (
                                                                <li
                                                                    key={`summary-${c.ref}`}
                                                                    id={getAiWardenCitationTargetId('file-insight', c)}
                                                                    className={`${styles.aiWardenCitationCard}${canOpenCitationPdf(c) ? ` ${styles.aiWardenCitationCardInteractive}` : ''}`}
                                                                    role={canOpenCitationPdf(c) ? 'button' : undefined}
                                                                    tabIndex={canOpenCitationPdf(c) ? 0 : undefined}
                                                                    onClick={() => canOpenCitationPdf(c) && handleCitationClick(c, fileInsightSummaryChunks)}
                                                                    onKeyDown={e => {
                                                                        if (!canOpenCitationPdf(c)) return;
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            e.preventDefault();
                                                                            handleCitationClick(c, fileInsightSummaryChunks);
                                                                        }
                                                                    }}>
                                                                    <span className={styles.aiWardenCitationRef}>{c.ref}</span>
                                                                    <span className={styles.aiWardenCitationTitle}>{getCitationDisplayTitle(c, linkedChunk, citationDisplayCtx)}</span>
                                                                    {getCitationSimilarityLabel(c, fileInsightSummaryChunks) && (
                                                                        <span className={styles.aiWardenCitationMeta}>
                                                                            {getCitationSimilarityLabel(c, fileInsightSummaryChunks)}
                                                                        </span>
                                                                    )}
                                                                    {(chunkDocType || chunkIntent) && (
                                                                        <div className={styles.aiWardenCitationKeywords} aria-label="Chunk metadata">
                                                                            {chunkDocType && <p>Type: {chunkDocType}</p>}
                                                                            {chunkIntent && <p>Intent: {chunkIntent}</p>}
                                                                        </div>
                                                                    )}
                                                                    {chunkTopics.length > 0 && (
                                                                        <div className={styles.aiWardenCitationKeywords} aria-label="Chunk topics">
                                                                            {chunkTopics.map(topic => (
                                                                                <span key={`${c.ref}-topic-${topic}`} className={styles.aiWardenKeywordChip}>
                                                                                    {topic}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {topKw.length > 0 && (
                                                                        <div className={styles.aiWardenCitationKeywords} aria-label="Chunk keywords">
                                                                            {topKw.map(kw => (
                                                                                <span key={`${c.ref}-kw-${kw}`} className={styles.aiWardenKeywordChip}>
                                                                                    {kw}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                    {tooltipText && (
                                                                        <span className={styles.aiWardenCitationTooltip} role="tooltip">
                                                                            {tooltipText.length > 700 ? `${tooltipText.slice(0, 700)}…` : tooltipText}
                                                                        </span>
                                                                    )}
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                ) : (
                                                    !fileInsightPanel.loading && <p className={styles.aiWardenInsightsMuted}>No citations returned for this summary.</p>
                                                )}
                                            </section>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : citationPanel ? (
                        <AiWardenPdfPanel
                            key={citationPanel.token}
                            file={citationPanel.doc}
                            highlightText={citationPanel.highlightText}
                            pageOneBased={citationPanel.pageOneBased}
                            onClose={() => setCitationPanel(null)}
                        />
                    ) : irlCitationPanel ? (
                        <IrlCitationSidebar
                            key={irlCitationPanel.token}
                            detail={irlCitationPanel.detail}
                            onClose={() => setIrlCitationPanel(null)}
                        />
                    ) : observationCitationPanel ? (
                        <ObservationCitationSidebar
                            key={observationCitationPanel.token}
                            detail={observationCitationPanel.detail}
                            onClose={() => setObservationCitationPanel(null)}
                        />
                    ) : null}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default AiWardenModal;
